/**
 * Admin Settings — Consolidated system configuration for Chief Officers.
 * Tabs: User Management, Positions & Units, Shift Configuration, Categories, Security, AI Features
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiFetch } from '../../lib/api';
import AppLayout from '../../components/layout/AppLayout';
import { ApiError } from '../../lib/api';
import {
  RBAC_PERMISSIONS,
  DEFAULT_ROLE_PERMISSIONS,
  DEFAULT_ROLES,
  MAX_ROLES,
  type RoleDefinition,
  type AzureGroupMapping,
} from '@shared/roles';
import {
  Settings as SettingsIcon,
  Users,
  Shield,
  Truck,
  Calendar,
  Tag,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Key,
  Lock,
  Check,
  X,
  Bot,
  Plus,
  ToggleLeft,
  ToggleRight,
  Pencil,
  Trash2,
  Flame,
  ClipboardList,
  BookOpen,
  Megaphone,
  Heart,
  Star,
  HelpCircle,
  Wrench,
  ScrollText,
  MessageSquare,
  Activity,
  Mail,
  Send,
  Eye,
  EyeOff,
  Clock,
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────

interface MemberInfo {
  id: number;
  display_name: string;
  email: string;
  battalion_id: number | null;
  member_type: string;
  status: string;
  azure_oid?: string;
  created_at?: string;
  updated_at?: string;
}

/** Detect if a member has never completed a real Azure AD login */
function isPendingActivation(member: MemberInfo): boolean {
  if (!member.azure_oid) return true;
  return member.azure_oid.startsWith('pending-') || member.azure_oid.startsWith('migration_');
}

interface RosterAssignment {
  id: number;
  battalion_id: number;
  unit_id: string;
  seat_id: string;
  member_id: number;
  display_name: string;
}

interface Position {
  id: number;
  name: string;
  code: string;
  category: string;
  sort_order: number;
  active: number;
}

interface Apparatus {
  id: number;
  name: string;
  type: string;
  sort_order: number;
  active: number;
}

interface Category {
  id: number;
  name: string;
  code: string;
  description: string;
  icon: string;
  sort_order: number;
  active: number;
}

interface Battalion {
  id: number;
  name: string;
  number: number;
  short_name: string | null;
}

interface ShiftConfig {
  id: number;
  name: string;
  code: string;
  description: string;
  shift_pattern: string;
  shift_duration_hours: number;
  cycle_days: number;
  num_shifts: number;
  start_time: string;
  anchor_date: string | null;
  color: string;
  is_primary: number;
  active: number;
}

type TabId =
  | 'users'
  | 'positions-units'
  | 'battalions'
  | 'shift-config'
  | 'categories'
  | 'identity'
  | 'rbac'
  | 'ai'
  | 'notifications'
  | 'health'
  | 'audit'
  | 'feedback';

// RBAC permissions, role definitions, and defaults imported from @shared/roles

// ── Constants ────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, typeof Flame> = {
  flame: Flame,
  'clipboard-list': ClipboardList,
  'book-open': BookOpen,
  megaphone: Megaphone,
  heart: Heart,
  star: Star,
  shield: Shield,
  truck: Truck,
  wrench: Wrench,
  tag: Tag,
};

const AVAILABLE_ICONS = Object.keys(ICON_MAP);

const SHIFT_PRESETS = [
  { label: '24/48 (Volunteer)', pattern: '24/48', duration: 24, cycle: 3, shifts: 3 },
  { label: '24/48 (3 Platoons)', pattern: '24/48', duration: 24, cycle: 3, shifts: 3 },
  { label: '24/72 (4 Platoons)', pattern: '24/72', duration: 24, cycle: 4, shifts: 4 },
  { label: '56-Hour (Paid)', pattern: '56hr', duration: 24, cycle: 3, shifts: 3 },
  { label: '48/96', pattern: '48/96', duration: 48, cycle: 6, shifts: 3 },
  { label: 'Custom', pattern: 'custom', duration: 24, cycle: 5, shifts: 5 },
];

interface TabDef {
  id: TabId;
  label: string;
  icon: typeof SettingsIcon;
  section?: string;
}

const TABS: TabDef[] = [
  { id: 'users', label: 'Users', icon: Users, section: 'Configuration' },
  { id: 'positions-units', label: 'Positions & Units', icon: Shield },
  { id: 'battalions', label: 'Battalions', icon: Flame },
  { id: 'shift-config', label: 'Shifts & Holidays', icon: Calendar },
  { id: 'categories', label: 'Categories', icon: Tag },
  { id: 'identity', label: 'Azure AD / Entra', icon: Key, section: 'Security' },
  { id: 'rbac', label: 'Roles & Permissions', icon: Lock },
  { id: 'ai', label: 'AI Features', icon: Bot },
  { id: 'notifications', label: 'Email Reports', icon: Mail },
  { id: 'health', label: 'System Health', icon: Activity, section: 'Monitoring' },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare },
];

// ── Main Component ───────────────────────────────────────────────────────

export default function AdminSettings() {
  const { user } = useAuth();
  // Support ?tab= query param for deep links
  const initialTab = (() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab') as TabId | null;
      if (t && TABS.some((tab) => tab.id === t)) return t;
    }
    return 'users' as TabId;
  })();
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [error, setError] = useState<string | null>(null);

  if (user?.role !== 'admin') {
    return (
      <AppLayout title="Settings">
        <div className="min-h-[50dvh] flex items-center justify-center text-content-tertiary text-base">
          Access denied. Admin privileges required.
        </div>
      </AppLayout>
    );
  }

  // Group tabs by section for sidebar rendering
  const sections: { label: string; tabs: TabDef[] }[] = [];
  let currentSection: { label: string; tabs: TabDef[] } | null = null;
  for (const tab of TABS) {
    if (tab.section) {
      currentSection = { label: tab.section, tabs: [tab] };
      sections.push(currentSection);
    } else if (currentSection) {
      currentSection.tabs.push(tab);
    }
  }

  return (
    <AppLayout title="Settings">
      <div className="w-full max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mt-3 mb-4">
          <SettingsIcon className="h-5 w-5 text-action-primary" />
          <h1 className="text-lg font-bold text-content-primary">Settings</h1>
        </div>

        {error && (
          <div
            className="mb-3 px-3 py-2 bg-danger-500/15 border border-danger-500/25 rounded-lg text-xs text-danger-400 cursor-pointer"
            onClick={() => setError(null)}
          >
            {error}
          </div>
        )}

        <div className="flex gap-5">
          {/* Left sidebar sub-menu */}
          <nav className="w-44 shrink-0 hidden md:block">
            <div className="sticky top-4 space-y-4">
              {sections.map((section) => (
                <div key={section.label}>
                  <div className="px-2 pb-1 text-[10px] font-bold text-content-tertiary uppercase tracking-widest">
                    {section.label}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {section.tabs.map((tab) => {
                      const Icon = tab.icon;
                      const active = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveTab(tab.id)}
                          className={`
                            flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-md
                            transition-colors duration-150 cursor-pointer text-left border-none
                            ${
                              active
                                ? 'bg-action-primary/10 text-action-primary font-semibold'
                                : 'text-content-secondary hover:bg-surface-sunken hover:text-content-primary bg-transparent'
                            }
                          `}
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </nav>

          {/* Mobile tab picker (horizontal scroll) */}
          <div className="md:hidden w-full mb-3 overflow-x-auto pb-1 -mt-1">
            <div className="flex gap-1">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border
                      transition-all duration-150 cursor-pointer whitespace-nowrap
                      ${
                        active
                          ? 'border-action-primary text-action-primary bg-action-primary/10 font-bold'
                          : 'border-edge-default text-content-secondary bg-surface-raised hover:text-content-primary'
                      }
                    `}
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'users' && <UserManagementTab />}
            {activeTab === 'positions-units' && <PositionsUnitsTab onError={setError} />}
            {activeTab === 'battalions' && <BattalionsTab onError={setError} />}
            {activeTab === 'shift-config' && <ShiftConfigTab onError={setError} />}
            {activeTab === 'categories' && <CategoriesTab onError={setError} />}
            {activeTab === 'identity' && <IdentityTab />}
            {activeTab === 'rbac' && <RBACTab />}
            {activeTab === 'ai' && <AIFeaturesTab />}
            {activeTab === 'notifications' && <NotificationsTab />}
            {activeTab === 'health' && <SystemHealthTab />}
            {activeTab === 'audit' && <AuditLogTab />}
            {activeTab === 'feedback' && <FeedbackTab />}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

// ── User Management Tab ──────────────────────────────────────────────────

function UserManagementTab() {
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [assignments, setAssignments] = useState<RosterAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [membersData, rosterData] = await Promise.all([
          apiFetch<{ data: MemberInfo[] }>('/api/admin/members'),
          apiFetch<{ data: RosterAssignment[] }>('/api/roster').catch(() => ({ data: [] })),
        ]);
        setMembers(membersData.data || []);
        setAssignments(rosterData.data || []);
      } catch {
        // Non-critical
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRosterSync = useCallback(async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await apiFetch<{
        message: string;
        total: number;
        created: number;
        updated: number;
        deactivated: number;
        skipped: number;
        errors: { email?: string; error: string }[];
      }>('/api/admin/sync-roster', { method: 'POST' });
      const parts = [`Sync complete: ${result.total} total`];
      if (result.created > 0) parts.push(`${result.created} created`);
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      if (result.deactivated > 0) parts.push(`${result.deactivated} deactivated`);
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
      if (result.errors.length > 0) parts.push(`${result.errors.length} errors`);
      setSyncMessage(parts.join(', '));
      // Refresh member list
      const membersData = await apiFetch<{ data: MemberInfo[] }>('/api/admin/members');
      setMembers(membersData.data || []);
    } catch (err) {
      setSyncMessage(err instanceof Error ? err.message : 'Roster sync failed');
    } finally {
      setSyncing(false);
    }
  }, []);

  const getRoleForMember = (member: MemberInfo): string => {
    if (member.member_type === 'admin') return 'admin';
    if (member.member_type === 'officer') return 'officer';
    return 'member';
  };

  const getMemberAssignments = (memberId: number): RosterAssignment[] => {
    return assignments.filter((a) => a.member_id === memberId);
  };

  const pendingCount = members.filter((m) => isPendingActivation(m)).length;
  const activeCount = members.filter((m) => m.status === 'active' && !isPendingActivation(m)).length;

  const filteredMembers = members.filter((m) => {
    const matchesSearch =
      searchQuery === '' ||
      m.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase());

    const role = getRoleForMember(m);
    const matchesRole = roleFilter === 'all' || role === roleFilter;

    const pending = isPendingActivation(m);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'pending' && pending) ||
      (statusFilter === 'active' && !pending && m.status === 'active') ||
      (statusFilter === 'inactive' && m.status !== 'active');

    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading users...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Search, filter, and roster sync */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-tertiary" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-surface-sunken border border-edge-default rounded-lg
                       text-content-primary outline-none focus:ring-2 focus:ring-edge-focus focus:border-transparent
                       placeholder:text-content-tertiary"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-surface-sunken border border-edge-default rounded-lg
                     text-content-primary outline-none focus:ring-2 focus:ring-edge-focus cursor-pointer"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="officer">Officer</option>
          <option value="member">Member</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-surface-sunken border border-edge-default rounded-lg
                     text-content-primary outline-none focus:ring-2 focus:ring-edge-focus cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="active">Active ({activeCount})</option>
          <option value="pending">Pending Activation ({pendingCount})</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          type="button"
          onClick={handleRosterSync}
          disabled={syncing}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border-none transition-colors duration-150 whitespace-nowrap
            ${
              syncing
                ? 'bg-surface-sunken text-content-tertiary cursor-default'
                : 'bg-action-primary text-content-on-primary cursor-pointer'
            }`}
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Importing...' : 'Import Members'}
        </button>
      </div>

      {syncMessage && <div className="text-xs text-content-secondary text-center py-1">{syncMessage}</div>}

      {/* Summary counts */}
      <div className="flex gap-3 flex-wrap">
        <div className="px-3 py-1.5 bg-surface-raised rounded-lg border border-edge-muted text-xs">
          <span className="text-content-tertiary">Total:</span>{' '}
          <span className="font-bold text-content-primary">{members.length}</span>
        </div>
        <div className="px-3 py-1.5 bg-surface-raised rounded-lg border border-edge-muted text-xs">
          <span className="text-content-tertiary">Active:</span>{' '}
          <span className="font-bold text-green-400">{activeCount}</span>
        </div>
        {pendingCount > 0 && (
          <div className="px-3 py-1.5 bg-amber-500/10 rounded-lg border border-amber-500/20 text-xs">
            <span className="text-amber-400">Pending Activation:</span>{' '}
            <span className="font-bold text-amber-400">{pendingCount}</span>
          </div>
        )}
        <div className="px-3 py-1.5 bg-surface-raised rounded-lg border border-edge-muted text-xs">
          <span className="text-content-tertiary">Showing:</span>{' '}
          <span className="font-bold text-content-primary">{filteredMembers.length}</span>
        </div>
      </div>

      {/* User list */}
      <div className="space-y-2">
        {filteredMembers.length === 0 ? (
          <div className="text-center p-8 text-content-tertiary text-sm">
            {searchQuery || roleFilter !== 'all' ? 'No users match your filters.' : 'No users found.'}
          </div>
        ) : (
          filteredMembers.map((member) => {
            const role = getRoleForMember(member);
            const memberAssignments = getMemberAssignments(member.id);
            const isExpanded = expandedUser === member.id;
            const pending = isPendingActivation(member);

            return (
              <div
                key={member.id}
                className={`bg-surface-raised rounded-xl border overflow-hidden ${
                  pending ? 'border-amber-500/30' : 'border-edge-default'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedUser(isExpanded ? null : member.id)}
                  className="w-full flex items-center gap-3 p-4 text-left bg-transparent border-none
                             cursor-pointer hover:bg-surface-sunken/50 transition-colors duration-150"
                >
                  <div
                    className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 ${
                      pending ? 'bg-amber-500/15 border-amber-500/30' : 'bg-action-primary/15 border-edge-muted'
                    }`}
                  >
                    <span className={`text-xs font-bold ${pending ? 'text-amber-400' : 'text-action-primary'}`}>
                      {member.display_name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-content-primary truncate">{member.display_name}</div>
                    <div className="text-xs text-content-tertiary truncate">{member.email}</div>
                  </div>

                  {pending && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md border bg-amber-500/15 text-amber-400 border-amber-500/25 whitespace-nowrap">
                      Pending Activation
                    </span>
                  )}
                  <RoleBadge role={role} />

                  {member.battalion_id && (
                    <span className="text-xs font-bold text-action-primary whitespace-nowrap">
                      Bn {member.battalion_id}
                    </span>
                  )}

                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-content-tertiary shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-content-tertiary shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-edge-muted">
                    {pending && (
                      <div className="mt-3 mb-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <div className="text-xs font-semibold text-amber-400">Pending Activation</div>
                        <div className="text-[11px] text-content-tertiary mt-0.5">
                          This member was imported from the roster but has not signed in via Azure AD yet. They will be
                          activated automatically on first login.
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <DetailField label="Member ID" value={String(member.id)} />
                      <DetailField label="Email" value={member.email} />
                      <DetailField label="Role" value={role.charAt(0).toUpperCase() + role.slice(1)} />
                      <DetailField
                        label="Battalion"
                        value={member.battalion_id ? `Battalion ${member.battalion_id}` : 'Unassigned'}
                      />
                      <DetailField label="Member Type" value={member.member_type} />
                      <DetailField label="Login Status" value={pending ? 'Never logged in' : 'Activated'} />
                      {member.created_at && (
                        <DetailField
                          label="Created"
                          value={new Date(member.created_at + 'Z').toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        />
                      )}
                      {member.updated_at && (
                        <DetailField
                          label="Last Updated"
                          value={new Date(member.updated_at + 'Z').toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        />
                      )}
                    </div>

                    <div className="mt-4">
                      <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">
                        Current Assignments
                      </div>
                      {memberAssignments.length > 0 ? (
                        <div className="space-y-1.5">
                          {memberAssignments.map((a) => (
                            <div
                              key={a.id}
                              className="flex items-center justify-between p-2.5 bg-surface-sunken rounded-lg"
                            >
                              <div className="text-xs text-content-primary">
                                <span className="font-semibold">{a.unit_id}</span>
                                <span className="text-content-tertiary"> / </span>
                                <span>{a.seat_id}</span>
                              </div>
                              <span className="text-[10px] text-content-tertiary">Bn {a.battalion_id}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-content-tertiary p-2.5 bg-surface-sunken rounded-lg">
                          No unit assignments
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Positions & Units Tab ────────────────────────────────────────────────

function PositionsUnitsTab({ onError }: { onError: (msg: string) => void }) {
  const [positions, setPositions] = useState<Position[]>([]);
  const [apparatus, setApparatus] = useState<Apparatus[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPositions = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: Position[] }>('/api/admin/positions');
      setPositions(res.data);
    } catch {
      console.error('Failed to fetch positions');
    }
  }, []);

  const fetchApparatus = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: Apparatus[] }>('/api/admin/apparatus');
      setApparatus(res.data);
    } catch {
      console.error('Failed to fetch apparatus');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchPositions(), fetchApparatus()]).finally(() => setLoading(false));
  }, [fetchPositions, fetchApparatus]);

  if (loading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading positions & units...</div>;
  }

  return (
    <div className="space-y-10">
      <PositionsSection positions={positions} onRefresh={fetchPositions} onError={onError} />
      <UnitsSection apparatus={apparatus} onRefresh={fetchApparatus} onError={onError} />
    </div>
  );
}

function PositionsSection({
  positions,
  onRefresh,
  onError,
}: {
  positions: Position[];
  onRefresh: () => void;
  onError: (msg: string) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newCategory, setNewCategory] = useState('general');

  async function togglePosition(id: number, currentActive: number) {
    try {
      await apiFetch(`/api/admin/positions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: currentActive === 0 }),
      });
      onRefresh();
    } catch {
      onError('Failed to update position');
    }
  }

  async function createPosition() {
    if (!newName.trim() || !newCode.trim()) return;
    try {
      await apiFetch('/api/admin/positions', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), code: newCode.trim(), category: newCategory }),
      });
      setNewName('');
      setNewCode('');
      setNewCategory('general');
      setShowAdd(false);
      onRefresh();
    } catch {
      onError('Failed to create position');
    }
  }

  async function deletePosition(id: number) {
    if (!confirm('Are you sure you want to remove this position?')) return;
    try {
      await apiFetch(`/api/admin/positions/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch {
      onError('Failed to delete position');
    }
  }

  async function saveEdit(id: number) {
    try {
      await apiFetch(`/api/admin/positions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editName, category: editCategory }),
      });
      setEditingId(null);
      onRefresh();
    } catch {
      onError('Failed to update position');
    }
  }

  function startEdit(p: Position) {
    setEditingId(p.id);
    setEditName(p.name);
    setEditCategory(p.category);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-content-primary">Positions</h3>
          <p className="text-xs text-content-tertiary mt-0.5">Manage riding positions available in dropdowns.</p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
        >
          <Plus className="h-3.5 w-3.5" /> Add Position
        </button>
      </div>

      {showAdd && (
        <div className="mb-4 p-4 bg-surface-raised border border-edge-default rounded-xl flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[120px]">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Engineer"
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
            />
          </div>
          <div className="w-28">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Code</label>
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="e.g. engineer"
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
            />
          </div>
          <div className="w-32">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Unit Type</label>
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none cursor-pointer"
            >
              <option value="general">General</option>
              <option value="engine">Engine</option>
              <option value="ambulance">Ambulance</option>
              <option value="medic">Medic</option>
              <option value="tower">Tower</option>
              <option value="rescue">Rescue</option>
              <option value="brush">Brush</option>
              <option value="utility">Utility</option>
              <option value="command">Command</option>
            </select>
          </div>
          <button
            onClick={createPosition}
            className="px-4 py-2 text-sm font-semibold bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
          >
            Save
          </button>
          <button
            onClick={() => setShowAdd(false)}
            className="px-3 py-2 text-sm text-content-secondary bg-surface-sunken rounded-lg cursor-pointer border border-edge-default"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Code</Th>
              <Th>Unit Type</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => (
              <tr
                key={p.id}
                className={`${p.active === 0 ? 'opacity-50' : ''} hover:bg-surface-sunken/50 transition-colors`}
              >
                <Td>
                  {editingId === p.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none w-full"
                    />
                  ) : (
                    p.name
                  )}
                </Td>
                <Td>
                  <code className="text-xs bg-surface-sunken px-1.5 py-0.5 rounded">{p.code}</code>
                </Td>
                <Td>
                  {editingId === p.id ? (
                    <select
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none cursor-pointer"
                    >
                      <option value="general">General</option>
                      <option value="engine">Engine</option>
                      <option value="ambulance">Ambulance</option>
                      <option value="tower">Tower</option>
                      <option value="rescue">Rescue</option>
                      <option value="command">Command</option>
                    </select>
                  ) : (
                    <span className="text-xs capitalize">{p.category}</span>
                  )}
                </Td>
                <Td>
                  <StatusBadge active={!!p.active} />
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {editingId === p.id ? (
                      <>
                        <button
                          onClick={() => saveEdit(p.id)}
                          className="p-1.5 rounded-lg bg-action-primary/15 text-action-primary cursor-pointer border-none"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 rounded-lg bg-surface-sunken text-content-tertiary cursor-pointer border-none"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(p)}
                          className="p-1.5 rounded-lg hover:bg-surface-sunken text-content-secondary cursor-pointer border-none bg-transparent"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <ToggleButton active={!!p.active} onClick={() => togglePosition(p.id, p.active)} />
                        <button
                          onClick={() => deletePosition(p.id)}
                          className="p-1.5 rounded-lg hover:bg-danger-500/15 text-danger-400 cursor-pointer border-none bg-transparent"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
            {positions.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-content-tertiary text-sm">
                  No positions configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnitsSection({
  apparatus,
  onRefresh,
  onError,
}: {
  apparatus: Apparatus[];
  onRefresh: () => void;
  onError: (msg: string) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('engine');

  async function toggleUnit(id: number, currentActive: number) {
    try {
      await apiFetch(`/api/admin/apparatus/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: currentActive === 0 }),
      });
      onRefresh();
    } catch {
      onError('Failed to update unit');
    }
  }

  async function createUnit() {
    if (!newName.trim()) return;
    try {
      await apiFetch('/api/admin/apparatus', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), type: newType }),
      });
      setNewName('');
      setNewType('engine');
      setShowAdd(false);
      onRefresh();
    } catch {
      onError('Failed to create unit');
    }
  }

  async function deleteUnit(id: number) {
    if (!confirm('Are you sure you want to remove this unit?')) return;
    try {
      await apiFetch(`/api/admin/apparatus/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch {
      onError('Failed to delete unit');
    }
  }

  async function saveEdit(id: number) {
    try {
      await apiFetch(`/api/admin/apparatus/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editName, type: editType }),
      });
      setEditingId(null);
      onRefresh();
    } catch {
      onError('Failed to update unit');
    }
  }

  function startEdit(a: Apparatus) {
    setEditingId(a.id);
    setEditName(a.name);
    setEditType(a.type);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-content-primary">Units</h3>
          <p className="text-xs text-content-tertiary mt-0.5">Manage units (apparatus) available in dropdowns.</p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
        >
          <Plus className="h-3.5 w-3.5" /> Add Unit
        </button>
      </div>

      {showAdd && (
        <div className="mb-4 p-4 bg-surface-raised border border-edge-default rounded-xl flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. E510"
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
            />
          </div>
          <div className="w-32">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Type</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none cursor-pointer"
            >
              <option value="engine">Engine</option>
              <option value="ambulance">Ambulance</option>
              <option value="medic">Medic</option>
              <option value="tower">Tower</option>
              <option value="rescue">Rescue</option>
              <option value="utility">Utility</option>
              <option value="brush">Brush</option>
            </select>
          </div>
          <button
            onClick={createUnit}
            className="px-4 py-2 text-sm font-semibold bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
          >
            Save
          </button>
          <button
            onClick={() => setShowAdd(false)}
            className="px-3 py-2 text-sm text-content-secondary bg-surface-sunken rounded-lg cursor-pointer border border-edge-default"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {apparatus.map((a) => (
              <tr
                key={a.id}
                className={`${a.active === 0 ? 'opacity-50' : ''} hover:bg-surface-sunken/50 transition-colors`}
              >
                <Td>
                  {editingId === a.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none w-full"
                    />
                  ) : (
                    a.name
                  )}
                </Td>
                <Td>
                  {editingId === a.id ? (
                    <select
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                      className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none cursor-pointer"
                    >
                      <option value="engine">Engine</option>
                      <option value="ambulance">Ambulance</option>
                      <option value="medic">Medic</option>
                      <option value="tower">Tower</option>
                      <option value="rescue">Rescue</option>
                      <option value="utility">Utility</option>
                      <option value="brush">Brush</option>
                    </select>
                  ) : (
                    <span className="text-xs font-medium bg-surface-sunken px-2 py-0.5 rounded-full capitalize">
                      {a.type}
                    </span>
                  )}
                </Td>
                <Td>
                  <StatusBadge active={!!a.active} />
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {editingId === a.id ? (
                      <>
                        <button
                          onClick={() => saveEdit(a.id)}
                          className="p-1.5 rounded-lg bg-action-primary/15 text-action-primary cursor-pointer border-none"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 rounded-lg bg-surface-sunken text-content-tertiary cursor-pointer border-none"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(a)}
                          className="p-1.5 rounded-lg hover:bg-surface-sunken text-content-secondary cursor-pointer border-none bg-transparent"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <ToggleButton active={!!a.active} onClick={() => toggleUnit(a.id, a.active)} />
                        <button
                          onClick={() => deleteUnit(a.id)}
                          className="p-1.5 rounded-lg hover:bg-danger-500/15 text-danger-400 cursor-pointer border-none bg-transparent"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
            {apparatus.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-8 text-content-tertiary text-sm">
                  No units configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Battalions Tab ────────────────────────────────────────────────────────

function BattalionsTab({ onError }: { onError: (msg: string) => void }) {
  const [battalions, setBattalions] = useState<Battalion[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchSucceeded, setFetchSucceeded] = useState(false);

  const [showAddBn, setShowAddBn] = useState(false);
  const [newBnName, setNewBnName] = useState('');
  const [newBnNumber, setNewBnNumber] = useState('');
  const [newBnShortName, setNewBnShortName] = useState('');
  const [editingBnId, setEditingBnId] = useState<number | null>(null);
  const [editBnName, setEditBnName] = useState('');
  const [editBnShortName, setEditBnShortName] = useState('');

  const fetchBattalions = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: Battalion[] }>('/api/admin/battalions');
      setBattalions(res.data ?? []);
      setFetchSucceeded(true);
    } catch {
      // API may return 404 if table doesn't exist yet — keep empty array
      setBattalions([]);
      setFetchSucceeded(false);
    }
  }, []);

  useEffect(() => {
    fetchBattalions().finally(() => setLoading(false));
  }, [fetchBattalions]);

  async function createBattalion() {
    if (!newBnName.trim() || !newBnNumber) return;
    try {
      await apiFetch('/api/admin/battalions', {
        method: 'POST',
        body: JSON.stringify({
          name: newBnName.trim(),
          number: parseInt(newBnNumber, 10),
          short_name: newBnShortName.trim() || undefined,
        }),
      });
      setNewBnName('');
      setNewBnNumber('');
      setNewBnShortName('');
      setShowAddBn(false);
      fetchBattalions();
    } catch {
      onError('Failed to create battalion');
    }
  }

  async function saveBnEdit(id: number) {
    try {
      await apiFetch(`/api/admin/battalions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editBnName, short_name: editBnShortName }),
      });
      setEditingBnId(null);
      fetchBattalions();
    } catch {
      onError('Failed to update battalion');
    }
  }

  async function deleteBattalion(id: number) {
    if (!confirm('Are you sure you want to remove this battalion?')) return;
    try {
      await apiFetch(`/api/admin/battalions/${id}`, { method: 'DELETE' });
      fetchBattalions();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete battalion');
    }
  }

  function startEditBn(bn: Battalion) {
    setEditingBnId(bn.id);
    setEditBnName(bn.name);
    setEditBnShortName(bn.short_name ?? `B${bn.number}`);
  }

  // The 5 known battalions + SOD that should always be shown
  const EXPECTED_BATTALIONS = [
    { number: 1, name: 'Battalion 1', short: 'B1' },
    { number: 2, name: 'Battalion 2', short: 'B2' },
    { number: 3, name: 'Battalion 3', short: 'B3' },
    { number: 4, name: 'Battalion 4', short: 'B4' },
    { number: 5, name: 'Battalion 5', short: 'B5' },
  ];

  // Determine which expected battalions are missing from the DB
  // Compare with loose equality to handle string/number type mismatches from API
  const missingBattalions = EXPECTED_BATTALIONS.filter(
    (exp) => !battalions.some((bn) => Number(bn.number) === exp.number || bn.name === exp.name),
  );

  if (loading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading battalions...</div>;
  }

  return (
    <div className="space-y-10">
      {/* Battalions Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-content-primary">Battalions</h3>
            <p className="text-xs text-content-tertiary mt-0.5">
              DCVFD operates 5 battalions in a rotating duty schedule, plus the Special Operations Division (SOD).
            </p>
          </div>
          <button
            onClick={() => setShowAddBn(!showAddBn)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
          >
            <Plus className="h-3.5 w-3.5" /> Add Battalion
          </button>
        </div>

        {missingBattalions.length > 0 && fetchSucceeded && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <div className="text-xs font-semibold text-amber-400 mb-1">Missing Battalions Detected</div>
            <div className="text-[11px] text-content-tertiary mb-2">
              The following expected battalions are not in the database:{' '}
              {missingBattalions.map((b) => b.name).join(', ')}
            </div>
            <button
              type="button"
              onClick={async () => {
                for (const b of missingBattalions) {
                  try {
                    await apiFetch('/api/admin/battalions', {
                      method: 'POST',
                      body: JSON.stringify({ name: b.name, number: b.number, short_name: b.short }),
                    });
                  } catch {
                    /* skip duplicates */
                  }
                }
                fetchBattalions();
              }}
              className="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-surface-base rounded-lg cursor-pointer border-none"
            >
              Create Missing Battalions
            </button>
          </div>
        )}

        {showAddBn && (
          <div className="mb-4 p-4 bg-surface-raised border border-edge-default rounded-xl flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[120px]">
              <label className="block text-[11px] font-semibold text-content-secondary mb-1">Name</label>
              <input
                type="text"
                value={newBnName}
                onChange={(e) => setNewBnName(e.target.value)}
                placeholder="Battalion 6"
                className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
              />
            </div>
            <div className="w-20">
              <label className="block text-[11px] font-semibold text-content-secondary mb-1">Number</label>
              <input
                type="number"
                min={1}
                value={newBnNumber}
                onChange={(e) => setNewBnNumber(e.target.value)}
                placeholder="6"
                className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
              />
            </div>
            <div className="w-24">
              <label className="block text-[11px] font-semibold text-content-secondary mb-1">Short Name</label>
              <input
                type="text"
                value={newBnShortName}
                onChange={(e) => setNewBnShortName(e.target.value)}
                placeholder="B6"
                className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
              />
            </div>
            <button
              onClick={createBattalion}
              className="px-4 py-2 text-sm font-semibold bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
            >
              Save
            </button>
            <button
              onClick={() => setShowAddBn(false)}
              className="px-3 py-2 text-sm text-content-secondary bg-surface-sunken rounded-lg cursor-pointer border border-edge-default"
            >
              Cancel
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Short Name</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {battalions.map((bn) => (
                <tr key={bn.id} className="hover:bg-surface-sunken/50 transition-colors">
                  <Td>
                    <span className="text-xs font-bold text-action-primary">{bn.number}</span>
                  </Td>
                  <Td>
                    {editingBnId === bn.id ? (
                      <input
                        type="text"
                        value={editBnName}
                        onChange={(e) => setEditBnName(e.target.value)}
                        className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none w-full"
                      />
                    ) : (
                      bn.name
                    )}
                  </Td>
                  <Td>
                    {editingBnId === bn.id ? (
                      <input
                        type="text"
                        value={editBnShortName}
                        onChange={(e) => setEditBnShortName(e.target.value)}
                        className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none w-20"
                      />
                    ) : (
                      <code className="text-xs bg-surface-sunken px-1.5 py-0.5 rounded">
                        {bn.short_name ?? `B${bn.number}`}
                      </code>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      {editingBnId === bn.id ? (
                        <>
                          <button
                            onClick={() => saveBnEdit(bn.id)}
                            className="p-1.5 rounded-lg bg-action-primary/15 text-action-primary cursor-pointer border-none"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingBnId(null)}
                            className="p-1.5 rounded-lg bg-surface-sunken text-content-tertiary cursor-pointer border-none"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditBn(bn)}
                            className="p-1.5 rounded-lg hover:bg-surface-sunken text-content-secondary cursor-pointer border-none bg-transparent"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => deleteBattalion(bn.id)}
                            className="p-1.5 rounded-lg hover:bg-danger-500/15 text-danger-400 cursor-pointer border-none bg-transparent"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
              {battalions.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-content-tertiary text-sm">
                    No battalions configured. Use the button above to create them.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SOD Section */}
      <div>
        <h3 className="text-base font-semibold text-content-primary mb-1">Special Operations Division (SOD)</h3>
        <p className="text-xs text-content-tertiary mb-3">
          SOD is a separate division with flexible assignment slots, not part of the regular battalion rotation.
        </p>
        <div className="p-4 bg-surface-raised rounded-xl border border-edge-default">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/15 flex items-center justify-center">
              <Star className="h-5 w-5 text-violet-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-content-primary">SOD - Special Operations Division</div>
              <div className="text-xs text-content-tertiary mt-0.5">Up to 30 flexible assignment slots</div>
            </div>
          </div>
        </div>
      </div>

      {/* Station Structure Reference */}
      <div>
        <h3 className="text-base font-semibold text-content-primary mb-1">Station Structure</h3>
        <p className="text-xs text-content-tertiary mb-3">
          Each battalion covers all 4 stations. This is the current station/unit structure.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { id: '510', name: 'Station 510', units: ['E510', 'A510', 'M510', 'R510', 'U510'] },
            { id: '513', name: 'Station 513', units: ['E513', 'A513', 'TW513', 'U513'] },
            { id: '518', name: 'Station 518', units: ['E518', 'A518', 'B518', 'U518'] },
            { id: '520', name: 'Station 520', units: ['E520', 'A520', 'M520', 'U520'] },
          ].map((station) => (
            <div key={station.id} className="p-3 bg-surface-raised rounded-xl border border-edge-default">
              <div className="text-xs font-bold text-content-primary mb-2">{station.name}</div>
              <div className="flex flex-wrap gap-1.5">
                {station.units.map((unit) => (
                  <span
                    key={unit}
                    className="px-2 py-0.5 text-[11px] font-medium bg-surface-sunken rounded-full text-content-secondary"
                  >
                    {unit}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Roster Approval Settings */}
      <RosterApprovalSection />
    </div>
  );
}

function RosterApprovalSection() {
  const [rbacConfig, setRbacConfig] = useState<Record<string, string[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<{ permissions: Record<string, string[]> }>('/api/admin/rbac-config');
        if (data.permissions) setRbacConfig(data.permissions);
      } catch {
        /* defaults */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const officerCanApprove = rbacConfig?.officer?.includes('approve_roster') ?? false;

  const toggleOfficerApproval = async () => {
    if (!rbacConfig) return;
    setSaving(true);
    setMessage(null);
    const officerPerms = rbacConfig.officer ?? [];
    const updatedPerms = officerCanApprove
      ? officerPerms.filter((p) => p !== 'approve_roster')
      : [...officerPerms, 'approve_roster'];
    const updated = { ...rbacConfig, officer: updatedPerms };
    try {
      await apiFetch('/api/admin/rbac-config', { method: 'PUT', body: JSON.stringify({ permissions: updated }) });
      setRbacConfig(updated);
      setMessage(officerCanApprove ? 'Officer approval disabled' : 'Officers can now approve roster changes');
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage('Failed to update approval settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div>
      <h3 className="text-base font-semibold text-content-primary mb-1">Roster Approval</h3>
      <p className="text-xs text-content-tertiary mb-3">
        Control who can approve or reject roster change proposals. Admins can always approve. Enable this to allow
        officers to approve as well when the chief is unavailable.
      </p>
      <div className="p-4 bg-surface-raised rounded-xl border border-edge-default">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-action-primary/10 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-action-primary" />
            </div>
            <div>
              <div className="text-sm font-semibold text-content-primary">Allow Officers to Approve Roster Changes</div>
              <div className="text-xs text-content-tertiary mt-0.5">
                {officerCanApprove
                  ? 'Officers can currently approve and reject roster change proposals.'
                  : 'Only admins can currently approve roster changes.'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleOfficerApproval}
            disabled={saving || !rbacConfig}
            className="shrink-0 cursor-pointer bg-transparent border-none p-0"
            aria-label={officerCanApprove ? 'Disable officer approval' : 'Enable officer approval'}
          >
            {officerCanApprove ? (
              <ToggleRight className="h-8 w-8 text-action-primary" />
            ) : (
              <ToggleLeft className="h-8 w-8 text-content-tertiary" />
            )}
          </button>
        </div>
        {message && <div className="mt-3 text-xs text-content-secondary">{message}</div>}
      </div>
    </div>
  );
}

// ── Shift Configuration Tab (Shifts & Holidays) ─────────────────────────

function ShiftConfigTab({ onError }: { onError: (msg: string) => void }) {
  const [shiftConfigs, setShiftConfigs] = useState<ShiftConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [newScheduleCode, setNewScheduleCode] = useState('');
  const [newScheduleDesc, setNewScheduleDesc] = useState('');
  const [newSchedulePreset, setNewSchedulePreset] = useState('24/48');
  const [newScheduleColor, setNewScheduleColor] = useState('#6366f1');

  const fetchShiftConfigs = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: ShiftConfig[] }>('/api/admin/shift-configs');
      setShiftConfigs(res.data);
    } catch {
      /* May not exist yet */
    }
  }, []);

  useEffect(() => {
    fetchShiftConfigs().finally(() => setLoading(false));
  }, [fetchShiftConfigs]);

  async function createSchedule() {
    if (!newScheduleName.trim() || !newScheduleCode.trim()) return;
    const preset = SHIFT_PRESETS.find((p) => p.pattern === newSchedulePreset) ?? SHIFT_PRESETS[0];
    try {
      await apiFetch('/api/admin/shift-configs', {
        method: 'POST',
        body: JSON.stringify({
          name: newScheduleName.trim(),
          code: newScheduleCode.trim(),
          description: newScheduleDesc,
          shift_pattern: preset.pattern,
          shift_duration_hours: preset.duration,
          cycle_days: preset.cycle,
          num_shifts: preset.shifts,
          color: newScheduleColor,
        }),
      });
      setNewScheduleName('');
      setNewScheduleCode('');
      setNewScheduleDesc('');
      setShowAddSchedule(false);
      fetchShiftConfigs();
    } catch {
      onError('Failed to create schedule config');
    }
  }

  async function toggleSchedule(id: number, currentActive: number) {
    try {
      await apiFetch(`/api/admin/shift-configs/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: currentActive === 0 }),
      });
      fetchShiftConfigs();
    } catch {
      onError('Failed to update schedule config');
    }
  }

  async function deleteSchedule(id: number) {
    if (!confirm('Are you sure you want to remove this schedule?')) return;
    try {
      await apiFetch(`/api/admin/shift-configs/${id}`, { method: 'DELETE' });
      fetchShiftConfigs();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete schedule config');
    }
  }

  if (loading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading shift configuration...</div>;
  }

  return (
    <div className="space-y-10">
      {/* Schedule Configurations Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-content-primary">Schedule Configurations</h3>
            <p className="text-xs text-content-tertiary mt-0.5">
              Configure shift schedules. Add overlay schedules (e.g. 56hr paid dept) that display on the calendar
              alongside the primary rotation.
            </p>
          </div>
          <button
            onClick={() => setShowAddSchedule(!showAddSchedule)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
          >
            <Plus className="h-3.5 w-3.5" /> Add Schedule
          </button>
        </div>

        {showAddSchedule && (
          <div className="mb-4 p-4 bg-surface-raised border border-edge-default rounded-xl space-y-3">
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[150px]">
                <label className="block text-[11px] font-semibold text-content-secondary mb-1">Name</label>
                <input
                  type="text"
                  value={newScheduleName}
                  onChange={(e) => setNewScheduleName(e.target.value)}
                  placeholder="Paid Dept 56hr"
                  className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
                />
              </div>
              <div className="w-36">
                <label className="block text-[11px] font-semibold text-content-secondary mb-1">Code</label>
                <input
                  type="text"
                  value={newScheduleCode}
                  onChange={(e) => setNewScheduleCode(e.target.value)}
                  placeholder="paid_56hr"
                  className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
                />
              </div>
              <div className="w-48">
                <label className="block text-[11px] font-semibold text-content-secondary mb-1">Shift Pattern</label>
                <select
                  value={newSchedulePreset}
                  onChange={(e) => setNewSchedulePreset(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none cursor-pointer"
                >
                  {SHIFT_PRESETS.map((p) => (
                    <option key={p.pattern + p.label} value={p.pattern}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-20">
                <label className="block text-[11px] font-semibold text-content-secondary mb-1">Color</label>
                <input
                  type="color"
                  value={newScheduleColor}
                  onChange={(e) => setNewScheduleColor(e.target.value)}
                  className="w-full h-[38px] cursor-pointer bg-transparent border-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-content-secondary mb-1">Description</label>
              <input
                type="text"
                value={newScheduleDesc}
                onChange={(e) => setNewScheduleDesc(e.target.value)}
                placeholder="Optional description"
                className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={createSchedule}
                className="px-4 py-2 text-sm font-semibold bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
              >
                Save
              </button>
              <button
                onClick={() => setShowAddSchedule(false)}
                className="px-3 py-2 text-sm text-content-secondary bg-surface-sunken rounded-lg cursor-pointer border border-edge-default"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {shiftConfigs.map((sc) => (
            <div key={sc.id} className="p-4 bg-surface-raised border border-edge-default rounded-xl">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-10 rounded-full" style={{ backgroundColor: sc.color }} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-content-primary">{sc.name}</span>
                      {sc.is_primary ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-action-primary/15 text-action-primary rounded">
                          PRIMARY
                        </span>
                      ) : null}
                      {!sc.active ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-surface-sunken text-content-tertiary rounded">
                          INACTIVE
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-content-tertiary mt-0.5">{sc.description || 'No description'}</div>
                    <div className="flex gap-3 mt-2">
                      <span className="text-[11px] text-content-secondary">
                        Pattern: <span className="font-semibold">{sc.shift_pattern}</span>
                      </span>
                      <span className="text-[11px] text-content-secondary">{sc.shift_duration_hours}hr shifts</span>
                      <span className="text-[11px] text-content-secondary">
                        {sc.num_shifts} shifts, {sc.cycle_days}-day cycle
                      </span>
                      <span className="text-[11px] text-content-secondary">Start: {sc.start_time}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <ToggleButton active={!!sc.active} onClick={() => toggleSchedule(sc.id, sc.active)} />
                  {!sc.is_primary && (
                    <button
                      onClick={() => deleteSchedule(sc.id)}
                      className="p-1.5 rounded-lg hover:bg-danger-500/15 text-danger-400 cursor-pointer border-none bg-transparent"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {shiftConfigs.length === 0 && (
            <p className="text-center text-content-tertiary text-sm py-8">
              No schedule configurations found. Add one above.
            </p>
          )}
        </div>
      </div>

      {/* Holidays Section (embedded in Shifts tab) */}
      <HolidaysSection onError={onError} />
    </div>
  );
}

// ── Categories Tab (with subcategories) ──────────────────────────────────

/** Subcategory definitions for each category — these define what options appear on the home page. */
const SUBCATEGORIES: Record<string, { label: string; items: string[] }> = {
  operational: { label: 'Positions & Units', items: ['Managed on the Positions & Units page'] },
  administrative: {
    label: 'Admin Subcategories',
    items: ['Meetings', 'Paperwork', 'Station Maintenance', 'Fleet Maintenance', 'Inventory', 'Other Admin'],
  },
  training: {
    label: 'Training Subcategories',
    items: ['Drills', 'Certifications', 'In-Service', 'Academy', 'External Course', 'Other Training'],
  },
  public_event: {
    label: 'Public Event Subcategories',
    items: ['Community Outreach', 'Fire Prevention', 'Station Tour', 'Parade/Detail', 'Fundraiser', 'Other Event'],
  },
};

function CategoriesTab({ onError }: { onError: (msg: string) => void }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newIcon, setNewIcon] = useState('tag');
  const [expandedCat, setExpandedCat] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: Category[] }>('/api/admin/categories');
      setCategories(res.data);
    } catch {
      /* Categories table may not exist yet */
    }
  }, []);

  useEffect(() => {
    fetchCategories().finally(() => setLoading(false));
  }, [fetchCategories]);

  const displayCategories: Category[] =
    categories.length > 0
      ? categories
      : [
          {
            id: 0,
            code: 'operational',
            name: 'Operational',
            description: 'On-duty shifts, riding positions',
            icon: 'flame',
            sort_order: 1,
            active: 1,
          },
          {
            id: 0,
            code: 'administrative',
            name: 'Administrative',
            description: 'Admin tasks, meetings, paperwork',
            icon: 'clipboard-list',
            sort_order: 2,
            active: 1,
          },
          {
            id: 0,
            code: 'training',
            name: 'Training',
            description: 'Training sessions, certifications, drills',
            icon: 'book-open',
            sort_order: 3,
            active: 1,
          },
          {
            id: 0,
            code: 'public_event',
            name: 'Public Event',
            description: 'Community events, public education',
            icon: 'megaphone',
            sort_order: 4,
            active: 1,
          },
        ];
  const isFromApi = categories.length > 0;

  async function createCategory() {
    if (!newName.trim() || !newCode.trim()) return;
    try {
      await apiFetch('/api/admin/categories', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), code: newCode.trim(), description: newDesc, icon: newIcon }),
      });
      setNewName('');
      setNewCode('');
      setNewDesc('');
      setNewIcon('tag');
      setShowAdd(false);
      fetchCategories();
    } catch {
      onError('Failed to create category');
    }
  }

  async function saveEdit(id: number) {
    try {
      await apiFetch(`/api/admin/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editName, description: editDesc, icon: editIcon }),
      });
      setEditingId(null);
      fetchCategories();
    } catch {
      onError('Failed to update category');
    }
  }

  async function toggleCategory(id: number, currentActive: number) {
    try {
      await apiFetch(`/api/admin/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: currentActive === 0 }),
      });
      fetchCategories();
    } catch {
      onError('Failed to update category');
    }
  }

  async function deleteCategory(id: number) {
    if (!confirm('Are you sure you want to remove this category?')) return;
    try {
      await apiFetch(`/api/admin/categories/${id}`, { method: 'DELETE' });
      fetchCategories();
    } catch {
      onError('Failed to delete category');
    }
  }

  function startEdit(cat: Category) {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditDesc(cat.description);
    setEditIcon(cat.icon);
  }

  if (loading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading categories...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-content-tertiary">
            Manage time entry categories. Changes here are reflected on the homepage category picker.
          </p>
          <p className="text-[10px] text-content-tertiary mt-0.5">
            For Operational: positions and units are managed on the Positions &amp; Units page to avoid duplication.
          </p>
          {!isFromApi && (
            <p className="text-[10px] text-warning-500 mt-0.5">
              Showing default categories. Run the 0016 migration to enable editing.
            </p>
          )}
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
        >
          <Plus className="h-3.5 w-3.5" /> Add Category
        </button>
      </div>

      {showAdd && (
        <div className="p-4 bg-surface-raised border border-edge-default rounded-xl flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[120px]">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Fundraiser"
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
            />
          </div>
          <div className="w-28">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Code</label>
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="fundraiser"
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
            />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Description</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Description"
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none"
            />
          </div>
          <div className="w-32">
            <label className="block text-[11px] font-semibold text-content-secondary mb-1">Icon</label>
            <select
              value={newIcon}
              onChange={(e) => setNewIcon(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none cursor-pointer"
            >
              {AVAILABLE_ICONS.map((icon) => (
                <option key={icon} value={icon}>
                  {icon}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={createCategory}
            className="px-4 py-2 text-sm font-semibold bg-action-primary text-content-on-primary rounded-lg cursor-pointer border-none"
          >
            Save
          </button>
          <button
            onClick={() => setShowAdd(false)}
            className="px-3 py-2 text-sm text-content-secondary bg-surface-sunken rounded-lg cursor-pointer border border-edge-default"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Category cards with expandable subcategories */}
      <div className="space-y-3">
        {displayCategories.map((cat) => {
          const IconComp = ICON_MAP[cat.icon] ?? HelpCircle;
          const subcat = SUBCATEGORIES[cat.code];
          const isExpanded = expandedCat === cat.code;

          return (
            <div
              key={cat.id || cat.code}
              className={`bg-surface-raised rounded-xl border overflow-hidden ${cat.active === 0 ? 'opacity-50 border-edge-muted' : 'border-edge-default'}`}
            >
              <div className="flex items-center gap-3 p-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-action-primary/10 shrink-0">
                  <IconComp className="h-5 w-5 text-action-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === cat.id ? (
                    <div className="flex flex-wrap gap-2 items-center">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none flex-1 min-w-[100px]"
                      />
                      <input
                        type="text"
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        className="px-2 py-1 text-sm bg-surface-sunken border border-edge-default rounded text-content-primary outline-none flex-1 min-w-[120px]"
                        placeholder="Description"
                      />
                      <select
                        value={editIcon}
                        onChange={(e) => setEditIcon(e.target.value)}
                        className="px-1 py-1 text-xs bg-surface-sunken border border-edge-default rounded text-content-primary outline-none cursor-pointer w-28"
                      >
                        {AVAILABLE_ICONS.map((icon) => (
                          <option key={icon} value={icon}>
                            {icon}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-content-primary">{cat.name}</span>
                        <code className="text-[10px] bg-surface-sunken px-1.5 py-0.5 rounded text-content-tertiary">
                          {cat.code}
                        </code>
                        <StatusBadge active={!!cat.active} />
                      </div>
                      <div className="text-xs text-content-tertiary mt-0.5">{cat.description}</div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isFromApi && editingId === cat.id ? (
                    <>
                      <button
                        onClick={() => saveEdit(cat.id)}
                        className="p-1.5 rounded-lg bg-action-primary/15 text-action-primary cursor-pointer border-none"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1.5 rounded-lg bg-surface-sunken text-content-tertiary cursor-pointer border-none"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : isFromApi ? (
                    <>
                      <button
                        onClick={() => startEdit(cat)}
                        className="p-1.5 rounded-lg hover:bg-surface-sunken text-content-secondary cursor-pointer border-none bg-transparent"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <ToggleButton active={!!cat.active} onClick={() => toggleCategory(cat.id, cat.active)} />
                      <button
                        onClick={() => deleteCategory(cat.id)}
                        className="p-1.5 rounded-lg hover:bg-danger-500/15 text-danger-400 cursor-pointer border-none bg-transparent"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : null}
                  {subcat && (
                    <button
                      type="button"
                      onClick={() => setExpandedCat(isExpanded ? null : cat.code)}
                      className="p-1.5 rounded-lg hover:bg-surface-sunken text-content-tertiary cursor-pointer border-none bg-transparent ml-1"
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Subcategories expandable section */}
              {isExpanded && subcat && (
                <div className="px-4 pb-4 border-t border-edge-muted">
                  <div className="mt-3">
                    <div className="text-[11px] font-bold text-content-secondary uppercase tracking-wider mb-2">
                      {subcat.label}
                    </div>
                    {cat.code === 'operational' ? (
                      <div className="p-3 bg-surface-sunken rounded-lg">
                        <p className="text-xs text-content-secondary">
                          Operational subcategories are the <strong>Positions</strong> (IO, DPO, FF, etc.) and{' '}
                          <strong>Units</strong> (E510, A513, etc.) managed on the{' '}
                          <strong>Positions &amp; Units</strong> settings page. This avoids duplicate configuration.
                        </p>
                        <p className="text-[11px] text-content-tertiary mt-1.5">
                          When a user selects &ldquo;OPS&rdquo; on the home page, they pick an apparatus and riding
                          position from those lists.
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {subcat.items.map((item) => (
                          <span
                            key={item}
                            className="px-2.5 py-1 text-xs font-medium bg-surface-sunken rounded-lg text-content-secondary border border-edge-muted"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Identity Tab (Azure AD / Entra Integration) ─────────────────────────

function IdentityTab() {
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [adLoading, setAdLoading] = useState(true);
  const [adSaving, setAdSaving] = useState(false);
  const [adMessage, setAdMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [rolesData, groupsData] = await Promise.all([
          apiFetch<RoleDefinition[]>('/api/admin/roles'),
          apiFetch<{ mappings: AzureGroupMapping[] }>('/api/admin/azure-groups'),
        ]);
        setRoles(rolesData.sort((a, b) => b.priority - a.priority));
        const map: Record<string, string> = {};
        for (const m of groupsData.mappings || []) {
          map[m.roleKey] = m.azureGroupId;
        }
        setMappings(map);
      } catch {
        setRoles(DEFAULT_ROLES);
      } finally {
        setAdLoading(false);
      }
    })();
  }, []);

  const updateMapping = (roleKey: string, value: string) => {
    setMappings((prev) => ({ ...prev, [roleKey]: value }));
  };

  const handleSaveAD = useCallback(async () => {
    setAdSaving(true);
    setAdMessage(null);
    try {
      const mappingArray: AzureGroupMapping[] = Object.entries(mappings)
        .filter(([, v]) => v.trim() !== '')
        .map(([roleKey, azureGroupId]) => ({ roleKey, azureGroupId: azureGroupId.trim() }));
      await apiFetch('/api/admin/azure-groups', {
        method: 'PUT',
        body: JSON.stringify({ mappings: mappingArray }),
      });
      setAdMessage('Azure AD group configuration saved successfully');
      setTimeout(() => setAdMessage(null), 3000);
    } catch (err) {
      setAdMessage(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setAdSaving(false);
    }
  }, [mappings]);

  const roleColorMap: Record<string, string> = {
    admin: 'text-danger-400',
    officer: 'text-warning-500',
    member: 'text-action-primary',
  };

  if (adLoading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading identity configuration...</div>;
  }

  return (
    <div className="space-y-10">
      <div>
        <div className="p-5 bg-surface-raised rounded-xl border border-edge-default">
          <div className="flex items-center gap-2 mb-1">
            <Key className="h-4 w-4 text-action-primary" />
            <h2 className="text-[15px] font-bold text-content-primary">Azure AD Group Mapping</h2>
          </div>
          <p className="text-xs text-content-tertiary mb-5">
            Configure the Azure AD security group Object IDs that map to each application role. Members of these groups
            will be automatically assigned the corresponding role when they sign in. Roles can be added on the RBAC tab.
          </p>

          <div className="space-y-4">
            {roles.map((role) => (
              <GroupIdField
                key={role.key}
                label={`${role.label} Group ID`}
                description={
                  role.key === 'member'
                    ? 'Optional. All authenticated users default to Member role.'
                    : `Members of this Azure AD group are assigned the ${role.label} role (priority ${role.priority})`
                }
                value={mappings[role.key] || ''}
                onChange={(val) => updateMapping(role.key, val)}
                roleColor={roleColorMap[role.key] || 'text-content-secondary'}
              />
            ))}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveAD}
              disabled={adSaving}
              className={`px-5 py-2.5 text-sm font-semibold rounded-lg border-none transition-colors duration-150
                ${
                  adSaving
                    ? 'bg-surface-sunken text-content-tertiary cursor-default'
                    : 'bg-action-primary text-content-on-primary cursor-pointer'
                }`}
            >
              {adSaving ? 'Saving...' : 'Save Configuration'}
            </button>
            {adMessage && <span className="text-xs text-content-secondary">{adMessage}</span>}
          </div>
        </div>

        <div className="mt-4 p-4 bg-surface-sunken rounded-xl border border-edge-muted">
          <div className="text-xs text-content-tertiary space-y-1.5">
            <p className="font-semibold text-content-secondary">How role assignment works:</p>
            <p>1. User signs in via Azure AD (Microsoft Entra ID)</p>
            <p>2. The system checks their group memberships against the Group IDs configured above</p>
            <p>3. The highest-priority matching role is assigned. If no match, defaults to Member.</p>
            <p>
              4. Group IDs can be found in Azure Portal &rarr; Microsoft Entra ID &rarr; Groups &rarr; select group
              &rarr; Object ID
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── RBAC Tab (Roles & Permissions) ──────────────────────────────────────

function RBACTab() {
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [permissions, setPermissions] = useState<Record<string, string[]>>(DEFAULT_ROLE_PERMISSIONS);
  const [rbacLoading, setRbacLoading] = useState(true);
  const [rbacSaving, setRbacSaving] = useState(false);
  const [rbacMessage, setRbacMessage] = useState<string | null>(null);

  // Add-role form state
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleKey, setNewRoleKey] = useState('');
  const [newRoleLabel, setNewRoleLabel] = useState('');
  const [newRolePriority, setNewRolePriority] = useState(25);
  const [rolesSaving, setRolesSaving] = useState(false);

  // Edit-role form state
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [rolesData, rbacData] = await Promise.all([
          apiFetch<RoleDefinition[]>('/api/admin/roles'),
          apiFetch<{ permissions: Record<string, string[]> }>('/api/admin/rbac-config'),
        ]);
        setRoles(rolesData.sort((a, b) => b.priority - a.priority));
        if (rbacData.permissions) setPermissions(rbacData.permissions);
      } catch {
        setRoles(DEFAULT_ROLES);
      } finally {
        setRbacLoading(false);
      }
    })();
  }, []);

  // Sorted roles for display: highest priority first
  const sortedRoles = [...roles].sort((a, b) => b.priority - a.priority);

  const togglePermission = (roleKey: string, permId: string) => {
    setPermissions((prev) => {
      const current = prev[roleKey] || [];
      const has = current.includes(permId);
      return {
        ...prev,
        [roleKey]: has ? current.filter((p) => p !== permId) : [...current, permId],
      };
    });
  };

  const handleSaveRBAC = useCallback(async () => {
    setRbacSaving(true);
    setRbacMessage(null);
    try {
      await apiFetch('/api/admin/rbac-config', {
        method: 'PUT',
        body: JSON.stringify({ permissions }),
      });
      setRbacMessage('RBAC permissions saved successfully');
      setTimeout(() => setRbacMessage(null), 3000);
    } catch (err) {
      setRbacMessage(err instanceof Error ? err.message : 'Failed to save RBAC configuration');
    } finally {
      setRbacSaving(false);
    }
  }, [permissions]);

  const resetToDefaults = () => setPermissions(DEFAULT_ROLE_PERMISSIONS);

  const handleAddRole = async () => {
    const key = newRoleKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!key || !newRoleLabel.trim()) return;
    if (roles.some((r) => r.key === key)) {
      setRbacMessage(`Role key "${key}" already exists`);
      return;
    }
    const newRole: RoleDefinition = {
      key,
      label: newRoleLabel.trim(),
      priority: newRolePriority,
      isSystem: false,
    };
    const updated = [...roles, newRole];
    setRolesSaving(true);
    try {
      await apiFetch('/api/admin/roles', { method: 'PUT', body: JSON.stringify(updated) });
      setRoles(updated);
      // Initialize permissions for new role (copy from member)
      setPermissions((prev) => ({ ...prev, [key]: [...(prev.member || [])] }));
      setShowAddRole(false);
      setNewRoleKey('');
      setNewRoleLabel('');
      setNewRolePriority(25);
    } catch (err) {
      setRbacMessage(err instanceof Error ? err.message : 'Failed to add role');
    } finally {
      setRolesSaving(false);
    }
  };

  const handleDeleteRole = async (roleKey: string) => {
    if (!confirm(`Delete the "${roleKey}" role? This cannot be undone.`)) return;
    const updated = roles.filter((r) => r.key !== roleKey);
    try {
      await apiFetch('/api/admin/roles', { method: 'PUT', body: JSON.stringify(updated) });
      setRoles(updated);
      setPermissions((prev) => {
        const next = { ...prev };
        delete next[roleKey];
        return next;
      });
    } catch (err) {
      setRbacMessage(err instanceof Error ? err.message : 'Failed to delete role');
    }
  };

  const handleEditRole = async (roleKey: string) => {
    const role = roles.find((r) => r.key === roleKey);
    if (!role || !editLabel.trim()) return;
    const updated = roles.map((r) => (r.key === roleKey ? { ...r, label: editLabel.trim() } : r));
    try {
      await apiFetch('/api/admin/roles', { method: 'PUT', body: JSON.stringify(updated) });
      setRoles(updated);
      setEditingRole(null);
    } catch (err) {
      setRbacMessage(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const categories = Array.from(new Set(RBAC_PERMISSIONS.map((p) => p.category)));
  const roleCount = sortedRoles.length;
  const gridCols = `1fr ${sortedRoles.map(() => '80px').join(' ')}`;

  if (rbacLoading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Loading permissions...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[15px] font-bold text-content-primary">Role-Based Access Control</h2>
          <p className="text-xs text-content-tertiary mt-0.5">
            Toggle functions and abilities for each role. Add custom roles to match your department structure.
          </p>
        </div>
        <div className="flex gap-2">
          {roleCount < MAX_ROLES && (
            <button
              type="button"
              onClick={() => setShowAddRole(true)}
              className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-action-primary bg-surface-raised
                         border border-action-primary/30 rounded-lg cursor-pointer hover:bg-action-primary/5
                         transition-colors duration-150"
            >
              <Plus className="h-3.5 w-3.5" /> Add Role
            </button>
          )}
          <button
            type="button"
            onClick={resetToDefaults}
            className="px-3 py-2 text-xs font-medium text-content-secondary bg-surface-raised
                       border border-edge-default rounded-lg cursor-pointer hover:bg-surface-sunken
                       transition-colors duration-150"
          >
            Reset to Defaults
          </button>
          <button
            type="button"
            onClick={handleSaveRBAC}
            disabled={rbacSaving}
            className={`px-4 py-2 text-xs font-semibold rounded-lg border-none transition-colors duration-150
              ${
                rbacSaving
                  ? 'bg-surface-sunken text-content-tertiary cursor-default'
                  : 'bg-action-primary text-content-on-primary cursor-pointer'
              }`}
          >
            {rbacSaving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>

      {rbacMessage && <div className="text-xs text-content-secondary text-center py-1 mb-3">{rbacMessage}</div>}

      {/* Add Role Form */}
      {showAddRole && (
        <div className="mb-4 p-4 bg-surface-raised rounded-xl border border-action-primary/30">
          <h3 className="text-sm font-semibold text-content-primary mb-3">Add Custom Role</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-[11px] font-medium text-content-tertiary mb-1">Key (unique ID)</label>
              <input
                type="text"
                value={newRoleKey}
                onChange={(e) => setNewRoleKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="captain"
                className="w-full px-2.5 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg
                           text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-content-tertiary mb-1">Display Name</label>
              <input
                type="text"
                value={newRoleLabel}
                onChange={(e) => setNewRoleLabel(e.target.value)}
                placeholder="Captain"
                className="w-full px-2.5 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg
                           text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-content-tertiary mb-1">Priority (0-99)</label>
              <input
                type="number"
                value={newRolePriority}
                onChange={(e) => setNewRolePriority(Math.max(1, Math.min(99, parseInt(e.target.value) || 0)))}
                min={1}
                max={99}
                className="w-full px-2.5 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg
                           text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
              />
            </div>
          </div>
          <div className="text-[11px] text-content-tertiary mb-3">
            Priority determines access level. Officer=50, Admin=100, Member=0. Roles with priority &ge;50 get
            officer-level features.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddRole}
              disabled={rolesSaving || !newRoleKey.trim() || !newRoleLabel.trim()}
              className="px-4 py-2 text-xs font-semibold rounded-lg border-none bg-action-primary
                         text-content-on-primary cursor-pointer disabled:bg-surface-sunken
                         disabled:text-content-tertiary disabled:cursor-default"
            >
              {rolesSaving ? 'Adding...' : 'Add Role'}
            </button>
            <button
              type="button"
              onClick={() => setShowAddRole(false)}
              className="px-4 py-2 text-xs font-medium text-content-secondary bg-surface-raised
                         border border-edge-default rounded-lg cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface-raised rounded-xl border border-edge-default overflow-hidden overflow-x-auto">
        <div
          className="grid gap-0 px-4 py-3 bg-surface-sunken border-b border-edge-muted"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div className="text-xs font-bold text-content-secondary uppercase tracking-wider">Permission</div>
          {sortedRoles.map((role) => (
            <div key={role.key} className="text-center">
              {editingRole === role.key ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleEditRole(role.key);
                      if (e.key === 'Escape') setEditingRole(null);
                    }}
                    className="w-16 px-1 py-0.5 text-[11px] bg-surface-sunken border border-edge-default rounded
                               text-content-primary outline-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => handleEditRole(role.key)}
                    className="text-action-primary cursor-pointer"
                  >
                    <Check className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="group relative">
                  <div className="text-xs font-bold text-content-secondary uppercase tracking-wider">{role.label}</div>
                  <div className="text-[9px] text-content-tertiary">p{role.priority}</div>
                  {!role.isSystem && (
                    <div className="absolute -top-1 -right-1 hidden group-hover:flex gap-0.5">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingRole(role.key);
                          setEditLabel(role.label);
                        }}
                        className="p-0.5 bg-surface-raised border border-edge-default rounded cursor-pointer
                                   hover:bg-surface-sunken"
                        title="Edit role"
                      >
                        <Pencil className="h-2.5 w-2.5 text-content-tertiary" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRole(role.key)}
                        className="p-0.5 bg-surface-raised border border-danger-300 rounded cursor-pointer
                                   hover:bg-danger-50"
                        title="Delete role"
                      >
                        <Trash2 className="h-2.5 w-2.5 text-danger-400" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {categories.map((category) => {
          const categoryPerms = RBAC_PERMISSIONS.filter((p) => p.category === category);
          return (
            <div key={category}>
              <div className="px-4 py-2 bg-surface-sunken/50 border-b border-edge-muted">
                <span className="text-[11px] font-bold text-content-tertiary uppercase tracking-widest">
                  {category}
                </span>
              </div>

              {categoryPerms.map((perm) => (
                <div
                  key={perm.id}
                  className="grid gap-0 px-4 py-2.5 border-b border-edge-muted
                               last:border-b-0 hover:bg-surface-sunken/30 transition-colors duration-100"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div>
                    <div className="text-sm text-content-primary">{perm.label}</div>
                    <div className="text-[11px] text-content-tertiary">{perm.description}</div>
                  </div>
                  {sortedRoles.map((role) => {
                    const enabled = permissions[role.key]?.includes(perm.id) ?? false;
                    return (
                      <div key={role.key} className="flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => togglePermission(role.key, perm.id)}
                          className={`
                              w-8 h-8 rounded-lg border flex items-center justify-center cursor-pointer
                              transition-all duration-150
                              ${
                                enabled
                                  ? 'bg-action-primary/15 border-action-primary text-action-primary'
                                  : 'bg-surface-sunken border-edge-default text-content-tertiary hover:border-edge-strong'
                              }
                            `}
                          aria-label={`${enabled ? 'Disable' : 'Enable'} ${perm.label} for ${role.label}`}
                        >
                          {enabled ? <Check className="h-4 w-4" /> : <X className="h-3.5 w-3.5 opacity-40" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AI Features Tab ─────────────────────────────────────────────────────

function AIFeaturesTab() {
  const [aiStatus, setAiStatus] = useState<{ available: boolean; model: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await apiFetch<{ status: string; bindings: Record<string, boolean> }>('/api/health');
        setAiStatus({
          available: true,
          model: '@cf/meta/llama-3.1-8b-instruct-fp8-fast',
        });
      } catch {
        setAiStatus({ available: false, model: '' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-center p-10 text-content-tertiary text-sm">Checking AI features...</div>;
  }

  const features = [
    {
      name: 'Open Position Candidate Finder',
      description:
        'When a shift needs coverage, AI identifies available candidates and explains why each is a practical choice to reach out to. Focuses on availability, battalion proximity, and responsiveness — not scoring or reliability evaluation.',
      location: 'Swaps & Coverage → Open Requests → Select a request → Review Panel',
    },
    {
      name: 'Coverage Assistant Chat',
      description:
        'Interactive AI assistant that helps officers find people to fill open positions. Can look up member availability, hours, and suggest outreach strategies.',
      location: 'Swaps & Coverage → Open Requests → Select a request → "AI Assistant" button',
    },
  ];

  return (
    <div className="max-w-3xl space-y-6">
      <div className="p-5 bg-surface-raised rounded-xl border border-edge-default">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="h-4 w-4 text-action-primary" />
          <h2 className="text-[15px] font-bold text-content-primary">Workers AI Status</h2>
        </div>
        <p className="text-xs text-content-tertiary mb-4">
          AI features are powered by Cloudflare Workers AI. They are best-effort — failures never block core workflows.
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-surface-sunken rounded-lg">
            <span className="text-sm text-content-secondary">AI Binding</span>
            <span className={`text-sm font-bold ${aiStatus?.available ? 'text-success-500' : 'text-danger-400'}`}>
              {aiStatus?.available ? 'Connected' : 'Not Available'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-surface-sunken rounded-lg">
            <span className="text-sm text-content-secondary">Model</span>
            <span className="text-sm font-mono text-content-primary">{aiStatus?.model || 'N/A'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {features.map((feature) => (
          <div key={feature.name} className="p-5 bg-surface-raised rounded-xl border border-edge-default">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-success-500" />
              <h3 className="text-sm font-bold text-content-primary">{feature.name}</h3>
            </div>
            <p className="text-xs text-content-secondary mb-3">{feature.description}</p>
            <div className="p-3 bg-surface-sunken rounded-lg">
              <div className="text-[11px] text-content-tertiary uppercase tracking-wider mb-1">How to access</div>
              <div className="text-xs text-content-primary">{feature.location}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-surface-sunken rounded-xl border border-edge-muted">
        <div className="text-xs text-content-tertiary space-y-1.5">
          <p className="font-semibold text-content-secondary">How AI helps fill open positions:</p>
          <p>1. When a shift needs coverage, the system identifies all eligible and available members.</p>
          <p>
            2. AI generates a short explanation for each candidate about why they're a good person to ask (proximity,
            availability, etc.).
          </p>
          <p>
            3. Officers review candidates and approve outreach via push notifications, Teams messages, text messages, or
            email.
          </p>
          <p>
            4. The AI Chat assistant can help officers strategize outreach — who to contact first, who's most likely
            available.
          </p>
          <p>
            5. All scoring and historical data is tracked internally for future analysis but is not the primary focus
            for officers.
          </p>
          <p className="font-semibold text-content-secondary pt-1">
            Access requirement: Officer or Admin role required to view coverage reviews and use AI features.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Shared Sub-components ────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2.5 text-xs font-semibold text-content-secondary uppercase tracking-wider border-b border-edge-default">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2.5 text-sm text-content-primary border-b border-edge-muted">{children}</td>;
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full
        ${active ? 'bg-success-500/15 text-success-400' : 'bg-surface-sunken text-content-tertiary'}
      `}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function ToggleButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                 bg-surface-sunken border border-edge-default rounded-lg cursor-pointer
                 hover:bg-action-secondary-hover transition-colors duration-150
                 text-content-secondary"
    >
      {active ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
      {active ? 'Deactivate' : 'Activate'}
    </button>
  );
}

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    admin: 'bg-danger-500/15 text-danger-400 border-danger-500/25',
    officer: 'bg-warning-500/15 text-warning-500 border-warning-500/25',
    member: 'bg-action-primary/15 text-action-primary border-action-primary/25',
  };
  const colorClass = colors[role] || colors.member;

  return (
    <span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-md border ${colorClass}`}>
      {role}
    </span>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-2.5 bg-surface-sunken rounded-lg">
      <div className="text-[10px] text-content-tertiary uppercase tracking-wider mb-0.5">{label}</div>
      <div className="text-sm text-content-primary">{value}</div>
    </div>
  );
}

function GroupIdField({
  label,
  description,
  value,
  onChange,
  roleColor,
}: {
  label: string;
  description: string;
  value: string;
  onChange: (val: string) => void;
  roleColor: string;
}) {
  return (
    <div>
      <label className="block">
        <span className={`text-sm font-semibold ${roleColor}`}>{label}</span>
        <span className="block text-[11px] text-content-tertiary mt-0.5 mb-1.5">{description}</span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="w-full px-3 py-2.5 text-sm bg-surface-sunken border border-edge-default rounded-lg
                     text-content-primary outline-none focus:ring-2 focus:ring-edge-focus focus:border-transparent
                     placeholder:text-content-tertiary font-mono"
        />
      </label>
    </div>
  );
}

// ── Audit Log Tab ─────────────────────────────────────────────────────────

interface AuditEntry {
  id: number;
  member_id: number | null;
  actor_name: string | null;
  action: string;
  entity_type: string;
  entity_id: number | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

interface AuditResponse {
  data: AuditEntry[];
  pagination: { page: number; limit: number; total: number; total_pages: number };
}

const AUDIT_ACTION_OPTIONS = [
  'clock_in',
  'clock_out',
  'edit_entry',
  'void_entry',
  'create_member',
  'update_member',
  'roster_sync',
  'schedule_generate',
  'schedule_override',
  'config_update',
  'holiday_update',
];

const AUDIT_ENTITY_OPTIONS = ['time_entry', 'member', 'schedule', 'config', 'holiday'];

function AuditLogTab() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
  const [filters, setFilters] = useState({ action: '', entity_type: '', member_id: '', start: '', end: '' });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchAuditLog = useCallback(
    async (page = 1) => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (filters.action) params.set('action', filters.action);
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.member_id) params.set('member_id', filters.member_id);
      if (filters.start) params.set('start', filters.start);
      if (filters.end) params.set('end', filters.end);
      try {
        const res = await apiFetch<AuditResponse>(`/api/admin/audit?${params}`);
        setEntries(res.data);
        setPagination(res.pagination);
      } catch (err) {
        console.error('Failed to fetch audit log:', err);
      }
    },
    [filters],
  );

  useEffect(() => {
    fetchAuditLog(1);
  }, [fetchAuditLog]);

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <select
          value={filters.action}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs"
        >
          <option value="">All actions</option>
          {AUDIT_ACTION_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={filters.entity_type}
          onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs"
        >
          <option value="">All entities</option>
          {AUDIT_ENTITY_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Member ID"
          value={filters.member_id}
          onChange={(e) => setFilters((f) => ({ ...f, member_id: e.target.value }))}
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs w-[90px]"
        />
        <input
          type="date"
          value={filters.start}
          onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs"
        />
        <input
          type="date"
          value={filters.end}
          onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs"
        />
      </div>

      <div className="text-content-tertiary text-[11px] mb-2">
        {entries.length} of {pagination.total} entries (page {pagination.page}/{pagination.total_pages || 1})
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Time</Th>
              <Th>Actor</Th>
              <Th>Action</Th>
              <Th>Entity</Th>
              <Th>ID</Th>
              <Th>Details</Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <Td>{fmtDate(entry.created_at)}</Td>
                <Td>{entry.actor_name ?? `#${entry.member_id ?? 'system'}`}</Td>
                <Td>
                  <span className="inline-block px-1.5 py-0.5 bg-surface-sunken rounded text-[11px]">
                    {entry.action}
                  </span>
                </Td>
                <Td>{entry.entity_type}</Td>
                <Td>{entry.entity_id ?? '\u2014'}</Td>
                <Td>
                  {(entry.old_values || entry.new_values) && (
                    <button
                      onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      className="px-1.5 py-0.5 bg-surface-sunken text-content-primary border-none rounded text-[11px] cursor-pointer"
                    >
                      {expandedId === entry.id ? 'Hide' : 'View'}
                    </button>
                  )}
                  {expandedId === entry.id && (
                    <div className="mt-1.5 p-2 bg-surface-sunken rounded-md max-w-[360px]">
                      {entry.old_values && (
                        <div>
                          <strong className="text-red-400 text-[11px]">Old:</strong>
                          <pre className="text-[11px] text-content-secondary whitespace-pre-wrap my-0.5">
                            {JSON.stringify(entry.old_values, null, 2)}
                          </pre>
                        </div>
                      )}
                      {entry.new_values && (
                        <div>
                          <strong className="text-green-400 text-[11px]">New:</strong>
                          <pre className="text-[11px] text-content-secondary whitespace-pre-wrap my-0.5">
                            {JSON.stringify(entry.new_values, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="sm:hidden flex flex-col gap-2">
        {entries.map((entry) => (
          <div key={entry.id} className="bg-surface-raised border border-edge-muted rounded-lg p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="inline-block px-1.5 py-0.5 bg-surface-sunken rounded text-[11px]">{entry.action}</span>
              <span className="text-[11px] text-content-tertiary">{fmtDate(entry.created_at)}</span>
            </div>
            <div className="text-xs text-content-primary mb-0.5">
              {entry.actor_name ?? `#${entry.member_id ?? 'system'}`}
            </div>
            <div className="text-[11px] text-content-secondary">
              {entry.entity_type} {entry.entity_id ? `#${entry.entity_id}` : ''}
            </div>
            {(entry.old_values || entry.new_values) && (
              <>
                <button
                  onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  className="mt-1.5 px-1.5 py-0.5 bg-surface-sunken text-content-primary border-none rounded text-[11px] cursor-pointer"
                >
                  {expandedId === entry.id ? 'Hide' : 'Details'}
                </button>
                {expandedId === entry.id && (
                  <div className="mt-1.5 p-2 bg-surface-sunken rounded-md">
                    {entry.old_values && (
                      <div>
                        <strong className="text-red-400 text-[11px]">Old:</strong>
                        <pre className="text-[11px] text-content-secondary whitespace-pre-wrap my-0.5">
                          {JSON.stringify(entry.old_values, null, 2)}
                        </pre>
                      </div>
                    )}
                    {entry.new_values && (
                      <div>
                        <strong className="text-green-400 text-[11px]">New:</strong>
                        <pre className="text-[11px] text-content-secondary whitespace-pre-wrap my-0.5">
                          {JSON.stringify(entry.new_values, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-3">
          <button
            onClick={() => fetchAuditLog(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="px-3 py-1.5 bg-action-primary text-content-on-primary border-none rounded-md cursor-pointer text-xs disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-content-secondary text-xs">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => fetchAuditLog(pagination.page + 1)}
            disabled={pagination.page >= pagination.total_pages}
            className="px-3 py-1.5 bg-action-primary text-content-on-primary border-none rounded-md cursor-pointer text-xs disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ── Holidays Section (embedded in Shifts & Holidays tab) ─────────────────

interface Holiday {
  id: number;
  date: string;
  name: string;
  created_at?: string;
}

function HolidaysSection({ onError }: { onError: (msg: string) => void }) {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear() + 1);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState<string | null>(null);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: Holiday[] }>('/api/admin/holidays');
      setHolidays(res.data);
    } catch (err) {
      console.error('Failed to fetch holidays:', err);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') fetchHolidays();
  }, [fetchHolidays, user]);

  // Auto-generate holidays only if no holidays exist at all (first-time setup)
  useEffect(() => {
    if (holidays.length > 0 || user?.role !== 'admin') return;
    (async () => {
      const currentYear = new Date().getFullYear();
      try {
        await apiFetch<{ generated: number; skipped: number }>('/api/admin/holidays/generate', {
          method: 'POST',
          body: JSON.stringify({ year: currentYear }),
        });
        await apiFetch<{ generated: number; skipped: number }>('/api/admin/holidays/generate', {
          method: 'POST',
          body: JSON.stringify({ year: currentYear + 1 }),
        });
        setGenerateMsg(`Auto-generated holidays for ${currentYear} and ${currentYear + 1}.`);
        fetchHolidays();
      } catch {
        /* Silent fail */
      }
    })();
  }, [holidays.length, user, fetchHolidays]);

  async function addHoliday(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    try {
      await apiFetch('/api/admin/holidays', { method: 'POST', body: JSON.stringify({ date: newDate, name: newName }) });
      setNewDate('');
      setNewName('');
      fetchHolidays();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Failed to add holiday');
    } finally {
      setAdding(false);
    }
  }

  async function removeHoliday(id: number) {
    try {
      await apiFetch(`/api/admin/holidays/${id}`, { method: 'DELETE' });
      fetchHolidays();
    } catch {
      onError('Failed to remove holiday');
    }
  }

  async function generateHolidays() {
    setGenerating(true);
    setGenerateMsg(null);
    try {
      const res = await apiFetch<{ generated: number; skipped: number }>('/api/admin/holidays/generate', {
        method: 'POST',
        body: JSON.stringify({ year: generateYear }),
      });
      setGenerateMsg(`Generated ${res.generated} holidays for ${generateYear} (${res.skipped} already existed).`);
      fetchHolidays();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Failed to generate holidays');
    } finally {
      setGenerating(false);
    }
  }

  function fmtDate(dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const holidaysByYear: Record<number, Holiday[]> = {};
  for (const h of holidays) {
    const year = parseInt(h.date.slice(0, 4), 10);
    if (!holidaysByYear[year]) holidaysByYear[year] = [];
    holidaysByYear[year].push(h);
  }
  const years = Object.keys(holidaysByYear).map(Number).sort();

  // Determine which future years don't have holidays yet (for smart generate suggestion)
  const currentYear = new Date().getFullYear();
  const yearsWithHolidays = new Set(years);
  const suggestYears = [currentYear, currentYear + 1].filter((y) => !yearsWithHolidays.has(y));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-content-primary">Holidays</h3>
          <p className="text-xs text-content-tertiary mt-0.5">
            Manage holidays that affect shift scheduling and pay calculations.
          </p>
        </div>
      </div>

      {/* Add holiday form */}
      <form onSubmit={addHoliday} className="flex flex-wrap gap-1.5 mb-4">
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          required
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs"
        />
        <input
          type="text"
          placeholder="Holiday name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          required
          className="px-2 py-1.5 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs flex-1 min-w-[140px]"
        />
        <button
          type="submit"
          disabled={adding}
          className="px-3 py-1.5 bg-green-700 text-white border-none rounded-md cursor-pointer text-xs font-semibold disabled:opacity-50"
        >
          {adding ? 'Adding...' : 'Add Holiday'}
        </button>
      </form>

      {/* Holiday list — shown first */}
      {holidays.length === 0 ? (
        <p className="text-content-tertiary italic text-center py-6 text-xs">No holidays configured.</p>
      ) : (
        years.map((year) => (
          <div key={year} className="mb-4">
            <h2 className="text-sm font-semibold text-content-primary mb-2 pb-1.5 border-b border-edge-muted">
              {year} ({holidaysByYear[year].length} holidays)
            </h2>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>Name</Th>
                    <Th>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {holidaysByYear[year].map((h) => (
                    <tr key={h.id}>
                      <Td>{fmtDate(h.date)}</Td>
                      <Td>{h.name}</Td>
                      <Td>
                        <button
                          onClick={() => removeHoliday(h.id)}
                          className="px-2 py-0.5 bg-red-900 text-white border-none rounded text-[11px] cursor-pointer"
                        >
                          Remove
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden flex flex-col gap-2">
              {holidaysByYear[year].map((h) => (
                <div
                  key={h.id}
                  className="bg-surface-raised border border-edge-muted rounded-lg p-2.5 flex items-center justify-between"
                >
                  <div>
                    <div className="text-xs text-content-primary font-medium">{h.name}</div>
                    <div className="text-[11px] text-content-secondary mt-0.5">{fmtDate(h.date)}</div>
                  </div>
                  <button
                    onClick={() => removeHoliday(h.id)}
                    className="px-2 py-0.5 bg-red-900 text-white border-none rounded text-[11px] cursor-pointer"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Auto-generate — at bottom, after configured holidays */}
      <div className="mt-6 p-3 bg-surface-raised border border-edge-default rounded-xl">
        <h2 className="text-xs font-bold text-content-primary mb-1.5">Generate Holidays for a New Year</h2>
        <p className="text-[11px] text-content-tertiary mb-2">
          Auto-generate standard US federal holidays. Existing holidays won&apos;t be duplicated.
          {suggestYears.length > 0 && (
            <span className="text-amber-400 ml-1">Missing holidays for: {suggestYears.join(', ')}</span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={2025}
            max={2035}
            value={generateYear}
            onChange={(e) => setGenerateYear(parseInt(e.target.value, 10))}
            className="px-2 py-1.5 w-20 bg-surface-sunken text-content-primary border border-edge-default rounded-md text-xs"
          />
          <button
            type="button"
            onClick={generateHolidays}
            disabled={generating}
            className="px-3 py-1.5 bg-action-primary text-content-on-primary border-none rounded-md cursor-pointer text-xs font-semibold disabled:opacity-50"
          >
            {generating ? 'Generating...' : `Generate ${generateYear} Holidays`}
          </button>
        </div>
        {generateMsg && <p className="text-[11px] text-green-400 mt-1.5">{generateMsg}</p>}
      </div>
    </div>
  );
}

// ── Feedback Tab ──────────────────────────────────────────────────────────

interface FeedbackItem {
  id: number;
  member_id: number;
  member_name: string;
  type: 'bug' | 'suggestion' | 'question';
  message: string;
  page: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface FeedbackResponse {
  data: FeedbackItem[];
  pagination: { page: number; limit: number; total: number; total_pages: number };
}

const FB_STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'] as const;
const FB_TYPE_BG: Record<string, string> = {
  bug: 'bg-red-500',
  suggestion: 'bg-violet-400',
  question: 'bg-action-primary',
};
const FB_STATUS_BG: Record<string, string> = {
  open: 'bg-amber-400',
  in_progress: 'bg-action-primary',
  resolved: 'bg-green-400',
  closed: 'bg-neutral-500',
};
const FB_STATUS_BORDER: Record<string, string> = {
  open: 'border-amber-400 text-amber-400',
  in_progress: 'border-action-primary text-action-primary',
  resolved: 'border-green-400 text-green-400',
  closed: 'border-content-tertiary text-content-tertiary',
};

function FeedbackTab() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [adminNotes, setAdminNotes] = useState('');

  const fetchFeedback = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.set('type', filterType);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      params.set('limit', '100');
      const res = await apiFetch<FeedbackResponse>(`/api/admin/feedback?${params}`);
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  async function updateStatus(id: number, newStatus: string, notes?: string) {
    try {
      await apiFetch(`/api/admin/feedback/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus, admin_notes: notes || undefined }),
      });
      fetchFeedback();
      setExpandedId(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Failed to update feedback:', err);
    }
  }

  function fmtDateTime(iso: string): string {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (loading) return <div className="text-center py-8 text-content-tertiary text-xs">Loading feedback...</div>;

  return (
    <div>
      <p className="text-xs text-content-tertiary mb-3">
        Track and manage all user-submitted feedback. Update status to keep the team informed on progress.
      </p>

      {/* Filters */}
      <div className="flex gap-3 mb-3">
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-content-tertiary uppercase tracking-wider">Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-2 py-1.5 text-xs bg-surface-raised text-content-primary border border-edge-default rounded-md"
          >
            <option value="all">All Types</option>
            <option value="bug">Bug</option>
            <option value="suggestion">Suggestion</option>
            <option value="question">Question</option>
          </select>
        </div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-content-tertiary uppercase tracking-wider">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 text-xs bg-surface-raised text-content-primary border border-edge-default rounded-md"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="flex items-end">
          <span className="text-[11px] text-content-tertiary">{items.length} items</span>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-content-tertiary italic text-center py-6 text-xs">No feedback matching these filters.</p>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <Th>Submitted</Th>
                  <Th>User</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Message</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-surface-sunken/50 transition-colors">
                    <Td>
                      <div className="text-[11px] text-content-secondary whitespace-nowrap">
                        {fmtDateTime(item.created_at)}
                      </div>
                    </Td>
                    <Td>
                      <div className="text-xs text-content-primary font-medium">{item.member_name}</div>
                    </Td>
                    <Td>
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase text-surface-base ${FB_TYPE_BG[item.type] || 'bg-neutral-500'}`}
                      >
                        {item.type}
                      </span>
                    </Td>
                    <Td>
                      <select
                        value={item.status}
                        onChange={(e) => updateStatus(item.id, e.target.value)}
                        className={`text-[11px] font-bold px-1.5 py-0.5 rounded border cursor-pointer bg-transparent ${FB_STATUS_BORDER[item.status] || 'border-content-tertiary text-content-tertiary'}`}
                      >
                        {FB_STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </Td>
                    <Td>
                      <div className="max-w-[300px]">
                        <div className="text-xs text-content-primary truncate">{item.message}</div>
                        {item.admin_notes && (
                          <div className="text-[10px] text-violet-400 truncate mt-0.5">Note: {item.admin_notes}</div>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedId(expandedId === item.id ? null : item.id);
                          setAdminNotes(item.admin_notes || '');
                        }}
                        className="px-2 py-1 text-[11px] font-medium bg-surface-sunken text-content-secondary border-none rounded cursor-pointer hover:bg-surface-raised"
                      >
                        {expandedId === item.id ? 'Close' : 'Details'}
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="sm:hidden flex flex-col gap-2">
            {items.map((item) => (
              <div key={item.id} className="bg-surface-raised border border-edge-muted rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-[9px] font-bold px-1 py-0.5 rounded uppercase text-surface-base ${FB_TYPE_BG[item.type] || 'bg-neutral-500'}`}
                    >
                      {item.type}
                    </span>
                    <span
                      className={`text-[9px] font-bold px-1 py-0.5 rounded uppercase text-surface-base ${FB_STATUS_BG[item.status] || 'bg-neutral-500'}`}
                    >
                      {item.status.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-[10px] text-content-tertiary">{fmtDateTime(item.created_at)}</span>
                </div>
                <div className="text-xs text-content-primary mb-1">{item.message}</div>
                <div className="text-[10px] text-content-tertiary">By: {item.member_name}</div>
                {item.admin_notes && <div className="text-[10px] text-violet-400 mt-1">Note: {item.admin_notes}</div>}
                <button
                  type="button"
                  onClick={() => {
                    setExpandedId(expandedId === item.id ? null : item.id);
                    setAdminNotes(item.admin_notes || '');
                  }}
                  className="mt-2 px-2 py-1 text-[11px] font-medium bg-surface-sunken text-content-secondary border-none rounded cursor-pointer"
                >
                  {expandedId === item.id ? 'Close' : 'Update Status'}
                </button>
              </div>
            ))}
          </div>

          {/* Expanded detail panel */}
          {expandedId !== null &&
            (() => {
              const item = items.find((i) => i.id === expandedId);
              if (!item) return null;
              return (
                <div className="mt-3 p-4 bg-surface-raised border border-edge-default rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase text-surface-base ${FB_TYPE_BG[item.type] || 'bg-neutral-500'}`}
                    >
                      {item.type}
                    </span>
                    <span className="text-xs font-semibold text-content-primary">Feedback #{item.id}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
                    <div>
                      <span className="text-content-tertiary">User:</span>{' '}
                      <span className="text-content-primary">{item.member_name}</span>
                    </div>
                    <div>
                      <span className="text-content-tertiary">Submitted:</span>{' '}
                      <span className="text-content-primary">{fmtDateTime(item.created_at)}</span>
                    </div>
                    <div>
                      <span className="text-content-tertiary">Page:</span>{' '}
                      <span className="text-content-primary">{item.page || '\u2014'}</span>
                    </div>
                    <div>
                      <span className="text-content-tertiary">Updated:</span>{' '}
                      <span className="text-content-primary">{fmtDateTime(item.updated_at)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-content-secondary leading-relaxed mb-3 p-2 bg-surface-sunken rounded-lg">
                    {item.message}
                  </p>
                  <div className="flex flex-col gap-2">
                    <textarea
                      placeholder="Admin notes..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={2}
                      className="w-full p-2 text-[11px] bg-surface-base text-content-primary border border-edge-default rounded-md font-[system-ui,sans-serif] resize-y box-border"
                    />
                    <div className="flex gap-1.5">
                      {FB_STATUS_OPTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateStatus(item.id, s, adminNotes || undefined)}
                          className={`px-2.5 py-1.5 text-[11px] font-semibold rounded cursor-pointer capitalize border ${
                            s === item.status
                              ? `${FB_STATUS_BG[s] || 'bg-neutral-500'} text-surface-base border-transparent`
                              : `bg-transparent ${FB_STATUS_BORDER[s] || 'border-content-tertiary text-content-tertiary'}`
                          }`}
                        >
                          {s.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
        </>
      )}
    </div>
  );
}

// ── System Health Tab ─────────────────────────────────────────────────────

interface HealthStatus {
  status: string;
  timestamp: string;
  bindings: { d1: boolean; kv: boolean; r2: boolean };
}

interface SystemStats {
  member_count: number;
  entry_count: number;
  migration_count: number;
  feedback_count: number;
  feedback_open: number;
  battalions: { id: number; name: string; member_count: number }[];
}

function SystemHealthTab() {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [healthData, statsData] = await Promise.all([
        apiFetch<HealthStatus>('/api/health'),
        apiFetch<SystemStats>('/api/admin/launch-status'),
      ]);
      setHealth(healthData);
      setStats(statsData);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  useEffect(() => {
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const allBindingsOk = health?.bindings.d1 && health?.bindings.kv && health?.bindings.r2;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-content-secondary">Real-time system monitoring</p>
          {lastRefresh && <p className="text-[10px] text-content-tertiary mt-0.5">Last updated: {lastRefresh}</p>}
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-content-secondary
                     bg-surface-raised border border-edge-default rounded-lg cursor-pointer
                     hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      <div className="mb-4">
        <div
          className={`flex items-center gap-2.5 p-3 rounded-xl border ${
            health?.status === 'ok' && allBindingsOk
              ? 'bg-green-900/20 border-green-500/30'
              : 'bg-red-900/20 border-red-500/30'
          }`}
        >
          <div
            className={`w-2.5 h-2.5 rounded-full ${health?.status === 'ok' && allBindingsOk ? 'bg-green-400' : 'bg-red-500'}`}
          />
          <div>
            <div
              className={`text-xs font-bold ${health?.status === 'ok' && allBindingsOk ? 'text-green-400' : 'text-red-500'}`}
            >
              {health?.status === 'ok' && allBindingsOk ? 'All Systems Operational' : 'System Issues Detected'}
            </div>
            <div className="text-[10px] text-content-tertiary mt-0.5">
              {health?.timestamp ? new Date(health.timestamp).toLocaleString() : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Bindings */}
      <div className="mb-4">
        <h2 className="text-xs font-semibold text-content-primary mb-2 pb-1.5 border-b border-edge-muted">
          Infrastructure Bindings
        </h2>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { label: 'D1 Database', ok: !!health?.bindings.d1 },
            { label: 'KV Store', ok: !!health?.bindings.kv },
            { label: 'R2 Bucket', ok: !!health?.bindings.r2 },
          ].map((b) => (
            <div key={b.label} className="bg-surface-raised border border-edge-muted rounded-lg p-2.5">
              <div className="text-[10px] text-content-tertiary uppercase tracking-wider">{b.label}</div>
              <div
                className={`text-xs font-bold mt-0.5 flex items-center gap-1 ${b.ok ? 'text-green-400' : 'text-red-500'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${b.ok ? 'bg-green-400' : 'bg-red-500'}`} />
                {b.ok ? 'Connected' : 'Error'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      {stats && (
        <>
          <div className="mb-4">
            <h2 className="text-xs font-semibold text-content-primary mb-2 pb-1.5 border-b border-edge-muted">
              Key Metrics
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {[
                { label: 'Active Members', value: stats.member_count },
                { label: 'Time Entries', value: stats.entry_count },
                { label: 'Total Feedback', value: stats.feedback_count },
                { label: 'Open Issues', value: stats.feedback_open, highlight: stats.feedback_open > 0 },
              ].map((m) => (
                <div key={m.label} className="bg-surface-raised border border-edge-muted rounded-lg p-2.5">
                  <div className="text-[10px] text-content-tertiary uppercase tracking-wider">{m.label}</div>
                  <div className={`text-lg font-bold mt-0.5 ${m.highlight ? 'text-amber-400' : 'text-action-primary'}`}>
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {stats.battalions.length > 0 && (
            <div className="mb-4">
              <h2 className="text-xs font-semibold text-content-primary mb-2 pb-1.5 border-b border-edge-muted">
                Battalion Enrollment
              </h2>
              <div className="flex flex-col gap-1">
                {stats.battalions.map((b) => (
                  <div key={b.id} className="flex justify-between px-2.5 py-1.5 bg-surface-raised rounded-md">
                    <span className="text-xs text-content-primary">{b.name}</span>
                    <span className="text-[11px] text-content-secondary">{b.member_count} members</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Notifications Tab ─────────────────────────────────────────────────────

interface NotificationTypeDescriptor {
  id: string;
  name: string;
  description: string;
  schedule: string;
  configPrefix: string;
  configEndpoint: string;
  testEndpoint: string;
  previewEndpoint: string;
}

const NOTIFICATION_TYPES: NotificationTypeDescriptor[] = [
  {
    id: 'daily_summary',
    name: 'Daily Shift Summary',
    description: 'Categorized time-entry report covering the prior 24-hour shift period.',
    schedule: 'Daily at 06:15 AM ET',
    configPrefix: 'daily_summary',
    configEndpoint: '/api/admin/notification-config',
    testEndpoint: '/api/admin/daily-summary/test',
    previewEndpoint: '/api/admin/daily-summary/preview',
  },
  {
    id: 'coverage_alerts',
    name: 'Uncovered Shift Alerts',
    description: 'Alerts when shifts remain uncovered within the next 48 hours.',
    schedule: 'Daily at 07:00 AM ET',
    configPrefix: 'coverage_alerts',
    configEndpoint: '/api/admin/coverage-alerts/config',
    testEndpoint: '/api/admin/coverage-alerts/test',
    previewEndpoint: '/api/admin/coverage-alerts/preview',
  },
];

function NotificationCard({ type }: { type: NotificationTypeDescriptor }) {
  const enabledKey = `${type.configPrefix}_enabled`;
  const recipientsKey = `${type.configPrefix}_recipients`;
  const extraEmailsKey = `${type.configPrefix}_extra_emails`;

  const [config, setConfig] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiFetch<Record<string, string>>(type.configEndpoint);
      setConfig(data);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [type.configEndpoint]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = async (updates: Record<string, string>) => {
    if (!config) return;
    setSaving(true);
    setError(null);
    try {
      const updated = { ...config, ...updates };
      await apiFetch(type.configEndpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      setConfig(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.includes('@')) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiFetch<{ ok: boolean; entries?: number; shifts?: number }>(type.testEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail }),
      });
      if (result.ok) {
        const count = result.entries ?? result.shifts ?? 0;
        setTestResult(`Test email sent (${count} ${count === 1 ? 'item' : 'items'} in report)`);
      } else {
        setTestResult('Failed to send test email');
      }
    } catch (err) {
      setTestResult(err instanceof ApiError ? err.message : 'Failed to send test');
    } finally {
      setTesting(false);
    }
  };

  const togglePreview = async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    if (!previewHtml) {
      setLoadingPreview(true);
      try {
        const data = await apiFetch<{ html: string }>(type.previewEndpoint);
        setPreviewHtml(data.html);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load preview');
        setLoadingPreview(false);
        return;
      } finally {
        setLoadingPreview(false);
      }
    }
    setShowPreview(true);
  };

  const extraEmails = config?.[extraEmailsKey]
    ? config[extraEmailsKey]
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
    : [];

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@') || !email.includes('.')) return;
    if (extraEmails.includes(email)) {
      setNewEmail('');
      return;
    }
    const updated = [...extraEmails, email].join(', ');
    saveConfig({ [extraEmailsKey]: updated });
    setNewEmail('');
  };

  const removeEmail = (emailToRemove: string) => {
    const updated = extraEmails.filter((e) => e !== emailToRemove).join(', ');
    saveConfig({ [extraEmailsKey]: updated });
  };

  if (loading) return <div className="p-5 text-content-secondary text-sm">Loading...</div>;

  const isEnabled = config?.[enabledKey] === '1';

  return (
    <div className="border border-edge-default rounded-lg p-5 space-y-5">
      {error && <div className="p-3 rounded-lg bg-danger-500/10 text-danger-500 text-sm">{error}</div>}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h3 className="font-medium text-content-primary">{type.name}</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-500 text-xs font-medium">
              <Clock className="w-3 h-3" />
              {type.schedule}
            </span>
          </div>
          <p className="text-sm text-content-secondary mt-1">{type.description}</p>
        </div>
        <button
          onClick={() => saveConfig({ [enabledKey]: isEnabled ? '0' : '1' })}
          disabled={saving}
          className="flex-shrink-0"
        >
          {isEnabled ? (
            <ToggleRight className="w-10 h-10 text-primary-500" />
          ) : (
            <ToggleLeft className="w-10 h-10 text-content-tertiary" />
          )}
        </button>
      </div>

      {/* Recipient Scope */}
      <div>
        <label className="text-sm font-medium text-content-primary block mb-1.5">Recipient Scope</label>
        <select
          value={config?.[recipientsKey] || 'officers'}
          onChange={(e) => saveConfig({ [recipientsKey]: e.target.value })}
          disabled={saving}
          className="w-full max-w-xs rounded-lg border border-edge-default bg-surface-base px-3 py-2 text-sm text-content-primary"
        >
          <option value="officers">Officers & Admins</option>
          <option value="admins">Admins Only</option>
          <option value="custom">Custom List Only</option>
        </select>
        <p className="text-xs text-content-tertiary mt-1">
          {config?.[recipientsKey] === 'custom'
            ? 'Only the email addresses listed below will receive the report.'
            : 'Members with the selected roles will receive the report, plus any additional addresses below.'}
        </p>
      </div>

      {/* Extra Email Recipients */}
      <div>
        <label className="text-sm font-medium text-content-primary block mb-1.5">Additional Recipients</label>
        <div className="flex gap-2 mb-2">
          <input
            type="email"
            placeholder="email@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addEmail()}
            className="flex-1 max-w-xs rounded-lg border border-edge-default bg-surface-base px-3 py-2 text-sm text-content-primary"
          />
          <button
            onClick={addEmail}
            disabled={saving || !newEmail.includes('@')}
            className="px-3 py-2 rounded-lg bg-action-primary text-content-on-primary text-sm font-medium hover:bg-action-primary-hover disabled:opacity-50 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
        {extraEmails.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {extraEmails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-sunken text-sm text-content-primary border border-edge-muted"
              >
                {email}
                <button
                  onClick={() => removeEmail(email)}
                  className="text-content-tertiary hover:text-danger-500 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-content-tertiary">No additional recipients configured.</p>
        )}
      </div>

      {/* Actions: Preview Template + Send Test */}
      <div className="border-t border-edge-muted pt-4 space-y-4">
        {/* Preview Template */}
        <div>
          <button
            onClick={togglePreview}
            disabled={loadingPreview}
            className="px-3 py-2 rounded-lg bg-surface-raised border border-edge-default text-sm font-medium text-content-primary hover:bg-surface-sunken disabled:opacity-50 flex items-center gap-1.5"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {loadingPreview ? 'Loading...' : showPreview ? 'Hide Preview' : 'Preview Template'}
          </button>
          {showPreview && previewHtml && (
            <div className="mt-3 border border-edge-default rounded-lg overflow-hidden bg-white">
              <iframe
                srcDoc={previewHtml}
                className="w-full border-0"
                style={{ minHeight: '400px' }}
                sandbox=""
                title={`${type.name} email preview`}
              />
            </div>
          )}
        </div>

        {/* Send Test */}
        <div>
          <label className="text-sm font-medium text-content-primary block mb-1.5">Send Test Email</label>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="your-email@dcvfd.org"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendTest()}
              className="flex-1 max-w-xs rounded-lg border border-edge-default bg-surface-base px-3 py-2 text-sm text-content-primary"
            />
            <button
              onClick={sendTest}
              disabled={testing || !testEmail.includes('@')}
              className="px-3 py-2 rounded-lg bg-surface-raised border border-edge-default text-sm font-medium text-content-primary hover:bg-surface-sunken disabled:opacity-50 flex items-center gap-1.5"
            >
              <Send className="w-4 h-4" />
              {testing ? 'Sending...' : 'Send Test'}
            </button>
          </div>
          {testResult && (
            <p className={`text-sm mt-2 ${testResult.includes('Failed') ? 'text-danger-500' : 'text-success-500'}`}>
              {testResult}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationsTab() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-content-primary">Email Notifications</h2>
        <p className="text-sm text-content-secondary mt-1">
          Configure automated email reports and alerts sent to department leadership.
        </p>
      </div>
      {NOTIFICATION_TYPES.map((type) => (
        <NotificationCard key={type.id} type={type} />
      ))}
    </div>
  );
}
